/**
 * NewWeatherMap.tsx — MapLibre version
 * الخريطة الرئيسية — تعمل بمحرك MapLibre GL بدلاً من Leaflet
 *
 * المكتبة: react-map-gl/maplibre  (مجانية، مفتوحة المصدر، مبنية على OpenStreetMap)
 * الخريطة: CartoDB Dark Matter (تصميم داكن مجاني مبني على OSM)
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker, type MapRef, type MapLayerMouseEvent } from 'react-map-gl/maplibre';
import { setMaxParallelImageRequests, setWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// ‏MapLibre 6 (ESM فقط) يحتاج مع المُجمِّعات نداءً واحداً لـ setWorkerUrl كي يجد عامله.
// ‏?worker&url لا ?url: العامل يستورد شقيقه maplibre-gl-shared.mjs، و?url ينسخ الملفّ حرفياً
// بلا ذلك الشقيق في بناء الإنتاج فيفشل العامل عند أوّل استيراد ولا تُحمَّل أي بلاطة
// (توثيق MapLibre الرسمي، قسم Vite).
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
setWorkerUrl(maplibreWorkerUrl);

/** صواعق التوقّع (o-index) فوق طبقة الأمطار — مُطفأة بطلب العميل، راجع موضع LightningLayer. */
const LIGHTNING_ON_PRECIP = false;

// الاستضافة المشتركة (LiteSpeed/PHP-FPM) تملك عدد عمّال محدوداً — الافتراضي 16 طلب
// بلاطة متزامن من MapLibre (خرائط الأساس + الأقمار الصناعية + الرادار) كان يتزاحم مع
// طابور إحماء الراستر على نفس الخادم، فتصطفّ طلبات الإطار الحالي الذي يحتاجه المستخدم
// الآن خلف عشرات الطلبات الخلفية — لوحظ فعلياً في أثر أداء حقيقي طلبات راستر تأخذ
// 15-25 ثانية بسبب هذا التزاحم تحديداً لا بطء الشبكة. حدّ أدنى يبقي الخادم قادراً على
// خدمة الطلب العاجل بسرعة دائماً.
//
// يبقى 8: المجموع مع prefetchQueue.ts (‏6 اليوم) = 14، وهو ما يراه الخادم فعلاً. الرقمان
// يتحرّكان معاً — إن خفّضت أحدهما فراجع الآخر.
// ‏MapLibre 6 لم يعد يصدّر config — الدالّة العامّة هي الطريق المدعوم (ومتاحة في 5 أيضاً).
setMaxParallelImageRequests(8);

import { LayerSidebar }     from './LayerSidebar';
import { TimeSlider }       from './TimeSlider';
import { frameAtEpoch, useRadarFrames } from './hooks/useRadarFrames';
import { domainInView, RADAR_STEP_MS, radarViewTileUrls } from '../../services/mrmsRadarService';
import { isLayerColorTemplateActive } from '../../config/remoteConfig';
import { WeatherInfoPanel } from './WeatherInfoPanel';
import { ModelSwitch } from './ModelSwitch';
import { MapLoadingToast } from './MapLoadingToast';
import { SplashScreen } from './SplashScreen';
import { WebGLUnsupported, isWebGLAvailable } from './WebGLUnsupported';
import { LightningLayer } from './LightningLayer';
import { GlmLightningLayer } from './GlmLightningLayer';
import { AlertPolygonLayer } from './AlertPolygonLayer';
import { PrecipitationAnimationWebGLLayer } from './PrecipitationAnimationWebGLLayer';
import { PressureAnnotationLayer } from './PressureAnnotationLayer';
import { IsobarWebGLLayer } from './IsobarWebGLLayer';
import { LayerControls }    from './LayerControls';
import { OverlayQuickControls } from './OverlayQuickControls';
import { TiledHeatmapWebGLLayer } from './TiledHeatmapWebGLLayer';
import { RasterHeatmapWebGLLayer } from './RasterHeatmapWebGLLayer';
import { HoverValueTooltip }    from './HoverValueTooltip';
import { PRECIP_ACCUMULATION_HOURS } from './hoverValueFormat';
import { MapTapCallout }        from './MapTapCallout';
import { ParticleWebGLLayer }  from './ParticleWebGLLayer';
import { ArabicCityLabels }    from './ArabicCityLabels';
import { RightToolbar }        from './RightToolbar';
import { CentralLegend, hasCentralLegend } from './CentralLegend';
import { TimestampBadge }     from './TimestampBadge';
import { CycloneTracker }      from './CycloneTracker';
import { RadarTileLayer }      from './RadarTileLayer';
import { radarGLCanAdvance }  from './RadarValuesGL';
import { SatelliteTileLayer }  from './SatelliteTileLayer';
import { MapContext }           from './MapContext';

import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore }    from '../../store/weatherStore';
import { perfCount } from '../../services/perfProbe';
import { detectIpLocation, readHomeLocation, saveHomeLocation } from '../../services/ipLocationService';
import { getMinZoom, getMaxZoom, getHomeZoom } from '../../config/mapZoom';

// علم على مستوى الوحدة (لا ref) كي لا يتكرر الكشف مع إعادة تركيب StrictMode.
let ipDetectionStarted = false;
import { isCompactViewport }  from '../../store/helpers';
import { useWeatherData }     from './hooks/useWeatherData';
import { weatherGridService } from '../../services/weatherGridService';
import { rasterSampler }      from '../../services/rasterSampler';
import { clearQueuedPrefetches, clearUrgentPrefetches, queuePrefetchImage } from '../../services/prefetchQueue';
import { isRasterImagePending, isRasterImageReady, isRasterMissing, prefetchRasterImage } from '../../services/rasterImageCache';
import { setPlaybackPosition } from '../../services/playbackClock';
import { setRasterVersion, setRasterExt, rasterUrl, layerFrameUrl, whenRasterVersionKnown } from '../../services/rasterVersion';
import { setReadinessSource, setReadinessFocus } from '../../services/rasterReadiness';
import { fetchRasterMeta, getCachedMeta, rasterDirFor, setCachedMeta } from '../../services/modelRunInfo';
import { getStableGridBounds } from './utils/gridBounds';
import { layerBaseIsDark, PRECIP_ANIM_LAYER_ID } from './webgl/layerOrder';
import { useAutoplay }        from './hooks/useAutoplay';
import { useDeferredComponent } from './hooks/useDeferredComponent';

// أدوات مخفيّة عند الدخول — حزمٌ منفصلة تُسخَّن بعد العرض الأوّل (راجع useDeferredComponent).
// الدوالّ على مستوى الوحدة لا داخل المكوّن: هويّتها مفتاح الذاكرة المشتركة.
const loadWildfire = () => import('./WildfireLayer').then((m) => m.WildfireLayer);
const loadMeasure = () => import('./MeasureTool').then((m) => m.MeasureTool);
const loadSettings = () => import('./SettingsPanel').then((m) => m.SettingsPanel);
import { useSatelliteFrames } from './hooks/useSatelliteFrames';
import { OCEAN_RASTER_DIR, useOceanFrames, useOceanUrl } from './hooks/useOceanFrames';
import { useMapStyling }      from './hooks/useMapStyling';
import { useTiledHeatmap }    from './hooks/useTiledHeatmap';
import { useWindRaster } from './hooks/useWindRaster';
import { BASE_SCALAR_LAYER_IDS, type ForecastGridType } from '../../config/weatherLayers';
import { FiMapPin }           from 'react-icons/fi';
import { useTranslation }      from '../../i18n/LanguageContext';

import './WeatherMap.css';

// ── خريطة الأساس — CartoDB Dark Matter (مبنية على OpenStreetMap، مجانية) ─────
const DARK_MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

/**
 * دورية التحقّق من دورة نموذج جديدة والصفحة مفتوحة. خمس دقائق: الملفّ بضع مئات من
 * البايتات (ورابطه مرقَّم بالدقيقة فلا يُثقل الحافة)، والدورة تُنشر أربع مرّات يومياً —
 * فالتأخّر الأقصى في التقاطها خمس دقائق بدل «إلى أن يُعيد المستخدم تحميل الصفحة».
 */
const RUN_RECHECK_MS = 5 * 60_000;

/**
 * شفافية طبقة الهطول فوق القاعدة.
 *
 * رُفعت من 0.85 إلى 0.94 بعد أن صارت القاعدة معتمة: عند 0.85 كان 15% من الزيتونيّ
 * يخترق كل بقعة مطر فيميل الأزرق الفاتح (أضعف فئة) إلى الأخضر ويختلط بالفئة التي
 * تليه. 0.94 يُبقي الألوان على قيمها في مفتاح الشريط تقريباً، ويترك أثراً طفيفاً
 * يمنع حوافّ البقع من أن تُقصّ كالورق المُقصوص.
 */
const PRECIP_OPACITY = 0.94;

// الطبقات ذات نسيج عالمي خام (GFS GRIB2 بدقّة 0.25° كاملة من NOAA، مجاناً، بلا Open-Meteo).
// كلّها متاحة في منتج GFS pgrb2 الأساسي. (الإحساس الحراري APTMP في المنتج الثانوي فيبقى
// على البلاطات؛ والرياح تحتاج قناتي U/V + جسيمات فتُعالَج لاحقاً.)
// ثابت على مستوى الوحدة كي لا يتغيّر مرجعه كل تصيير فيُعيد اشتراك التشغيل بلا داعٍ.
const RASTER_TYPES = new Set<string>([
    'temperature', 'feels-like', 'wet-bulb', 'k-index', 'o-index', 'dewpoint', 'humidity', 'pressure', 'clouds', 'wind-gusts', 'precipitation',
    'radar-sim',
    // شذوذ سطح البحر: نسيج عالمي واحد لكل إطار تماماً كبقيّة الطبقات السلَّمية — نفس
    // مسار العرض والمزج والإحماء، ولا يفرّقه عنها إلا مجلّده ومحوره الزمني.
    'sst-anomaly', 'sst-anomaly-rel',
    // شذوذ 850 هكتوباسكال: نسيج عالمي عادي من نفس دورة النموذج ومجلّده — لا شيء يميّزه
    // في مسار العرض. إغفاله هنا لا يُنتج خطأً بل **طبقةً تُضاء في القائمة ولا تُرسم**.
    't850-anomaly',
]);

// روابط النُسج التي أُطلق إحماؤها (Image) — يمنع إعادة إنشاء طلب لكل إطار مجاور عند كل
// تغيّر وقت. يُفرَّغ عند امتلائه فيُعاد الإحماء بما يتوافق مع كاش المتصفح (30 دقيقة).
const warmedRasters = new Set<string>();

/**
 * يستخرج نسيج (data URL) تراكم الأمطار لثلاث ساعات (rasterSampler.getAccumulatedFrameUrl)
 * ليُلوَّن به هيتماب الأمطار بدل نسيج المعدّل اللحظي الخام — فيطابق لون الخريطة نفس
 * الرقم المعروض في التلميح تماماً (أسلوب Windy). النسيج غير جاهز فوراً (يحتاج ساعتين
 * سابقتين محمَّلتين)؛ نعيد المحاولة كل إطار حتى يجهز، بنفس اتفاقية "أعد المحاولة لاحقاً"
 * المستخدمة في rasterSampler كله.
 */
/** ثلاثي متّسق: الإطاران المعروضان والفهرس الذي يخصّهما. */
interface PrecipPair {
    url: string;
    urlNext: string;
    /** فهرس الإطار الذي يمثّله `url` — **لا** موضع الشريط. */
    frameIndex: number;
}

/**
 * نسيجا تراكم الأمطار (الحالي والتالي) **كوحدة واحدة لا تتفكّك**.
 *
 * ── لماذا لا خطّافان مستقلّان ────────────────────────────────────────────────────
 *
 * كان `url` و`urlNext` يأتيان من نسختين مستقلّتين من خطّاف واحد، لكلٍّ حالتها. وبناء
 * نسيج التراكم غير فوري (يحتاج ثلاث ساعات هطول محمَّلة + حلقة على مليون بكسل)، فكانت
 * كل نسخة تتأخّر وحدها. النتيجة ثلاثة أعطال متزامنة عند كل عبور ساعة:
 *
 *  ❶ **زوج غير متجانس**: `url` من الساعة N-1 بينما `urlNext` وصل إلى N. فالطبقة تمزج
 *    (N-1 → N) لا (N → N+1) — أي تعرض الماضي.
 *  ❷ **مزج على أساس خاطئ**: `frameIndex` كان يُمرَّر من `currentTimeIndex` (الشريط)،
 *    فتُحسب نسبة المزج لساعة N بينما النسيجان يمثّلان N-1 وN. أي كسرٌ صحيح مطبَّق على
 *    زوج خاطئ — ساعة كاملة من الإزاحة الزمنية.
 *  ❸ **الرجّة**: حين يلحق الخطّاف المتأخّر، يقفز الحقل ساعةً كاملة دفعة واحدة. وهذا
 *    حرفياً ما وُصف بـ«الألوان تظهر ثم تعود عند التحكّم بالـtimeline».
 *
 * العلاج: لا نُسلّم شيئاً حتى يجهز **الطرفان معاً**، ونُسلّم معهما الفهرس الذي يخصّهما
 * فيُحسب المزج على أساسه. وحتى يجهزا نُبقي الزوج السابق كما هو — أقدم بساعة لكنه
 * **متّسق داخلياً**، فالحركة تبقى سلسة بلا قفزة للخلف.
 */
function usePrecipAccumPair(active: boolean, idx: number, lastIdx: number): PrecipPair | null {
    const [pair, setPair] = useState<PrecipPair | null>(null);
    useEffect(() => {
        if (!active) { setPair(null); return; }
        let rafId: number | null = null;
        let cancelled = false;
        const nextIdx = Math.min(idx + 1, lastIdx);
        const tick = () => {
            const cur = rasterSampler.getAccumulatedFrameUrl('precipitation', idx, PRECIP_ACCUMULATION_HOURS);
            const nxt = nextIdx === idx
                ? cur
                : rasterSampler.getAccumulatedFrameUrl('precipitation', nextIdx, PRECIP_ACCUMULATION_HOURS);
            // الطرفان أو لا شيء — الالتزام الجزئي هو ما كان يُنتج الزوج غير المتجانس.
            if (cur && nxt) {
                if (!cancelled) setPair({ url: cur, urlNext: nxt, frameIndex: idx });
                return;
            }
            rafId = window.requestAnimationFrame(tick);
        };
        tick();
        return () => { cancelled = true; if (rafId !== null) window.cancelAnimationFrame(rafId); };
    }, [active, idx, lastIdx]);
    return pair;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function NewWeatherMap() {
    const { t } = useTranslation();
    const {
        currentLocation,
        selectedModel,
        visibleLayers,
        darkMode,
        weatherData,
        currentTimeIndex,
        frameFraction,
        isPlaying,
        playbackSpeed,
        settingsOpen,
        setSettingsOpen,
        searchOpen,
        isobarsEnabled,
        aerialImageryEnabled,
        alertsEnabled,
        infoPanelOpen,
        layerControlsOpen,
        sidebarOpen,
        modalOpen,
        cycloneDockOpen,
        locationPreviewRequest,
        mapBounds,
        zoomLevel,
        particlesEnabled,
        setCurrentLocation,
        setInfoPanelOpen,
        setCurrentTimeIndex,
        setFrameFraction,
        timelineAutoSync,
        setMapBounds,
        setZoomLevel,
        initializeModels,
        layerAnimationSettings,
        timelineSeeking,
    } = useWeatherStore(useShallow((s) => ({ timelineSeeking: s.timelineSeeking, currentLocation: s.currentLocation, selectedModel: s.selectedModel, visibleLayers: s.visibleLayers, darkMode: s.darkMode, weatherData: s.weatherData, currentTimeIndex: s.currentTimeIndex, frameFraction: s.frameFraction, isPlaying: s.isPlaying, playbackSpeed: s.playbackSpeed, settingsOpen: s.settingsOpen, setSettingsOpen: s.setSettingsOpen, searchOpen: s.searchOpen, isobarsEnabled: s.isobarsEnabled, aerialImageryEnabled: s.aerialImageryEnabled, alertsEnabled: s.alertsEnabled, infoPanelOpen: s.infoPanelOpen, layerControlsOpen: s.layerControlsOpen, sidebarOpen: s.sidebarOpen, modalOpen: s.modalOpen, cycloneDockOpen: s.cycloneDockOpen, locationPreviewRequest: s.locationPreviewRequest, mapBounds: s.mapBounds, zoomLevel: s.zoomLevel, particlesEnabled: s.particlesEnabled, setCurrentLocation: s.setCurrentLocation, setInfoPanelOpen: s.setInfoPanelOpen, setCurrentTimeIndex: s.setCurrentTimeIndex, setFrameFraction: s.setFrameFraction, timelineAutoSync: s.timelineAutoSync, setMapBounds: s.setMapBounds, setZoomLevel: s.setZoomLevel, initializeModels: s.initializeModels, layerAnimationSettings: s.layerAnimationSettings })));
    // تُطلب فوراً إن احتاجها الزائر قبل التسخين (حرائق محفوظة مفعّلة، قياس، إعدادات).
    const needFires = useWeatherStore((s) => s.showWildfires || !!s.visibleLayers.wildfires);
    const needMeasure = useWeatherStore((s) => s.measureMode !== 'none');
    const WildfireLayer = useDeferredComponent(loadWildfire, needFires);
    const MeasureTool = useDeferredComponent(loadMeasure, needMeasure);
    const SettingsPanel = useDeferredComponent(loadSettings, settingsOpen);

    perfCount('React: NewWeatherMap render');

    /*
     * الجسيمات لا ترافق الرادار — والمفتاح لا يُمسّ.
     *
     * الرادار صدىً مرصود: ما تراه فيه هطولٌ وقع فعلاً، وحقل الرياح فوقه من نموذجٍ
     * متنبّئ لا علاقة له بلحظة الرصد. فتسيل جسيماتٌ متوقَّعة فوق مطرٍ حقيقي، وهما
     * زمنان مختلفان على خريطة واحدة.
     *
     * والإخفاء هنا في العرض لا في المخزن عمداً: لو أطفأنا `particlesEnabled` عند
     * دخول الرادار لضاع اختيار المستخدم — من شغّلها على الحرارة يجدها مطفأة حين
     * يعود إليها، ومن أطفأها يجدها مشتعلة. فالمفتاح يبقى كما تركه صاحبه، والرادار
     * وحده يمتنع عن الرسم ما دام معروضاً.
     */
    const particlesOn = particlesEnabled && !visibleLayers.radar;

    // هل يظهر شريط المفتاح أعلى الشاشة الآن؟ يقرّره CentralLegend وحده (مصدر واحد)،
    // ويحدّد موضع الشعار على الجوّال.
    const legendPresent = hasCentralLegend(visibleLayers);

    const mapRef = useRef<MapRef | null>(null);

    // على الموبايل: النقر يُظهر أولاً فقاعة قيمة صغيرة (أسلوب Windy) بدل فتح اللوحة الكاملة
    // مباشرةً؛ الضغط على سهمها هو ما يفتح WeatherInfoPanel. راجع MapTapCallout.
    const [tapLocation, setTapLocation] = useState<{ lat: number; lon: number } | null>(null);
    const handledLocationPreviewRequest = useRef(0);

    useEffect(() => {
        if (locationPreviewRequest === handledLocationPreviewRequest.current) return;
        if (!locationPreviewRequest || !currentLocation || !isCompactViewport()) return;
        handledLocationPreviewRequest.current = locationPreviewRequest;
        setInfoPanelOpen(false);
        setTapLocation({ lat: currentLocation.lat, lon: currentLocation.lon });
    }, [locationPreviewRequest, currentLocation, setInfoPanelOpen]);

    // ── Init ───────────────────────────────────────────────────────────────
    useEffect(() => { initializeModels(); }, []);

    // أول زيارة فقط (لا "مدينة مستخدم" محفوظة): كشف صامت عبر IP، حفظها كموقع افتتاحي
    // دائم، ثم التمركز عليها. الزيارات اللاحقة تفتح مباشرة على المدينة المحفوظة
    // (المتجر يقرأها لحظة الإنشاء) — لا على آخر إحداثيات تنقّل إليها المستخدم.
    useEffect(() => {
        if (ipDetectionStarted || readHomeLocation()) return;
        // زائرٌ جاء من رابطٍ عميق (صفحة مدينة) يريد **تلك** المدينة: الكشف بالـIP
        // يبقى ليُحفظ موقعه، لكنه لا يسحب الكاميرا من تحته إلى مدينته هو.
        const fromLink = new URLSearchParams(window.location.search).has('lat');
        ipDetectionStarted = true;
        detectIpLocation().then((loc) => {
            if (!loc) return;
            saveHomeLocation(loc);
            useWeatherStore.getState().setCurrentLocation(loc.lat, loc.lon);
            if (!fromLink) {
                mapRef.current?.flyTo({ center: [loc.lon, loc.lat], zoom: getHomeZoom(), duration: 1500 });
            }
        });
    }, []);

    /*
     * النموذج الفعّال. القمر الصناعي **رصد** لا مُخرَج نموذج، فلا معنى لـ ICON/ECMWF معه:
     * يعمل التطبيق على GFS طوال تفعيله (وهو مجلّد النُسج الجذر الذي تعيش فيه صور القمر).
     *
     * نشتقّه ولا نكتب في المتجر عمداً: الكتابة تدهس اختيار المستخدم المحفوظ، فيلزم بعدها
     * تذكّره واستعادته يدوياً — وأي مسار خروج غير متوقَّع (إغلاق التبويب وهو على القمر)
     * يُضيعه نهائياً. بالاشتقاق يبقى selectedModel = ICON كما تركه المستخدم، فيعود وحده
     * لحظة العودة إلى أي طبقة أخرى.
     */
    const effectiveModel = visibleLayers.satellite ? 'GFS' : selectedModel;

    // ── خطافات الجلب والتنسيق (مُستخرَجة لإبقاء الملف < 300 سطر) ──────────────
    useWeatherData({ currentLocation, selectedModel: effectiveModel });

    // بيان الرادار يُقرأ هنا لا في موضعه السابق أسفل الملفّ: منتقي الطبقة السلَّمية
    // أدناه صار يحتاج طول الأرشيف ليعرف أين ينتهي الرصد ويبدأ التوقّع.
    const {
        manifest: radarManifest, mosaic: radarMosaic,
        coverageTiles: radarCoverageTiles, times: radarTimes, observedCount: radarObserved,
        flow: radarFlow,
    } = useRadarFrames(visibleLayers.radar);

    // الطبقة السلَّمية الفعّالة (حرارة/ضغط…) تُشتقّ من الطبقات المرئية مباشرةً — بلا أي
    // جلب نقاط من Open-Meteo (مصدر العرض نُسج PNG محلّية).
    const activeHeatmapType: ForecastGridType | null = useMemo(() => {
        // من BASE_SCALAR_LAYER_IDS لا FORECAST_LAYER_IDS: هذه القائمة هي نفسها التي
        // يتبادل بها setActiveLayer، فيستحيل أن تُشغَّل طبقة لا يلتقطها هذا المنتقي.
        const heatmapTypes = BASE_SCALAR_LAYER_IDS.filter(
            (l): l is ForecastGridType => l !== 'wind' && l !== 'radar' && l !== 'satellite',
        );
        return heatmapTypes.find((t) => visibleLayers[t]) ?? null;
    }, [visibleLayers]);

    // نُسج خام متاحة للنماذج الثلاثة: GFS (NOAA) في rasters/، ECMWF (IFS) في rasters/ecmwf/،
    // و ICON (DWD) في rasters/icon/. التبديل يغيّر مجلّد النسيج فعلياً → بيانات نموذج مختلف،
    // بلا أي طلبات Open-Meteo (لا 429).
    const rasterDir = rasterDirFor(effectiveModel);
    // قارئ التلميح/تسميات المدن يتبع نفس المجلّد — وإلا عرض قيم GFS مع نموذج آخر.
    //
    // useLayoutEffect لا useEffect: React ينفّذ تأثيرات الأبناء قبل تأثيرات الأب، فكان
    // هذا السطر يعمل **بعد** أن تكون الطبقات الأبناء قد طلبت حقولها بالفعل — أي من مجلد
    // النموذج السابق — ثم يُفرغ الكاش من تحتها. وتأثيرات التخطيط كلها تسبق التأثيرات
    // العادية مهما كان موضعها في الشجرة، فيثبت المجلد الجديد قبل أن يقرأ أي ابن.
    useLayoutEffect(() => { rasterSampler.setDir(rasterDir); }, [rasterDir]);
    // الوضعان (مطلق/نسبي) يعيشان في نفس المجلّد وعلى نفس المحور الزمني، ويختلفان في
    // اسم الملفّ فقط — واسم النوع **هو** اسم الملفّ، فلا حاجة لأي ترجمة بينهما.
    const isOceanLayer = activeHeatmapType === 'sst-anomaly' || activeHeatmapType === 'sst-anomaly-rel';
    /*
     * يُعرَّفان هنا لا عند بناء المحور أدناه: `playbackCanAdvance` و مؤثّر الإحماء
     * يقرآن `oceanUrl` داخل **مصفوفات اعتمادياتهما**، وهي تُقيَّم أثناء الرسم لا مؤجّلة
     * كأجسام الدوالّ — فتعريفٌ لاحق يعني ReferenceError (TDZ) يُسقط الصفحة كاملةً.
     * نفس الفخّ الموثّق عند hasWindSpeed و isPrecipActive في هذا الملفّ.
     */
    const oceanMeta = useOceanFrames(visibleLayers['sst-anomaly']);
    const oceanUrl = useOceanUrl(oceanMeta);
    // مصدر طبقة المحيط للقارئ (التلميح وتسميات المدن): مجلّدها ومانيفستها لا مجلّد
    // النموذج — راجع setLayerSource. يُسجَّل فور وصول المانيفست ويُزال عند غيابه.
    useLayoutEffect(() => {
        rasterSampler.setLayerSource('sst-anomaly', oceanMeta ? {
            dir: OCEAN_RASTER_DIR,
            ext: oceanMeta.ext === 'webp' ? 'webp' : 'png',
            version: oceanMeta.generated_epoch ?? 0,
        } : null);
    }, [oceanMeta]);
    // شرط النموذج لا يُقيّد طبقة المحيط: مصدرها NOAA يومياً لا دورة نموذج، فتُعرض مهما
    // كان النموذج المختار (وتبقى قائمة النماذج مرئية لأن طبقات أخرى قد تُفتح بعدها).
    const useRaster = !!activeHeatmapType && RASTER_TYPES.has(activeHeatmapType)
        && (isOceanLayer || effectiveModel === 'GFS' || effectiveModel === 'ECMWF' || effectiveModel === 'ICON');

    // حقل الرياح من النسيج المحلّي (R=سرعة، G/B=U/V) — يغذّي الجسيمات وطبقة لون الرياح
    // وتلميح الرياح بلا Open-Meteo، ويتبع مجلّد النموذج المختار.
    // الساعات الثلاث المحيطة: الجسيمات تمزج بينها على الـ GPU زمنياً بشكل متّصل، وتحتاج
    // الساعة السابقة أيضاً لأن زمنها المعروض يتأخّر قليلاً عن المتجر أثناء التدرّج.

    // بلاطات الطبقة العددية الفعّالة (لغير طبقات النسيج) — تُجلب وتُعرض مربّعاً مربّعاً.
    const scalarTiles = useTiledHeatmap({
        mapBounds, mapZoom: zoomLevel ?? 4, selectedModel: effectiveModel, currentTimeIndex,
        activeType: useRaster ? null : activeHeatmapType,
    });

    // محور زمني مستقلّ عن Open-Meteo: ساعات توقّع GFS (نسيج لكلٍّ منها). نقرأ زمن دورة
    // النموذج من rasters/meta.json كي يطابق رقم الإطار (f000..fNN) زمن صلاحيته الحقيقي،
    // فلا يظهر إطار الليل (f000) تحت تسمية "الآن". يبقى الشريط يعمل حتى بلا Open-Meteo.
    const [rasterMeta, setRasterMeta] = useState<{ run_epoch: number; hours: number; step_hours?: number; hourly_through?: number; layers?: string[]; ext?: string } | null>(null);
    /** آخر دورة طُبِّقت فعلاً — مقياس «هل تغيّرت الدورة؟» في التحقّق الدوري أدناه. */
    const lastRunEpochRef = useRef<number | null>(null);
    /** وطول الشريط معه: الدورة تُنشر تدريجياً (راجع raster-publish-partial)، فتنمو
     *  `hours` بينما `run_epoch` ثابت — ولو راقبنا الدورة وحدها لبقي الزائر على أوّل
     *  اثنتي عشرة ساعة حتى نهاية الشوط. */
    const lastHoursRef = useRef<number | null>(null);
    /**
     * أي مجلّد نموذج تخصّه rasterMeta الحالية.
     *
     * ضروري لأن `setRasterMeta(null)` داخل مؤثّر الجلب لا يسري إلّا في الرسم التالي: ففي
     * نفس اللحظة التي يتغيّر فيها rasterDir يعمل مؤثّر المزامنة و rasterMeta **ما زالت
     * تخصّ النموذج السابق**. بلا هذا الحارس تُعاد المزامنة على دورة قديمة (عملية بلا أثر)
     * ويُستهلك الزمن المثبَّت، فلا تحدث إعادة الربط الحقيقية حين تصل meta الجديدة.
     */
    const [metaDir, setMetaDir] = useState<string | null>(null);
    // لا نبني روابط نُسج قبل معرفة رقم الدورة (?v) — رابط بلا ترقيم يُخزَّن يوماً كاملاً.
    const [versionKnown, setVersionKnown] = useState(false);
    // يُضبط بشكل متزامن مع setRasterMeta (لا عبر وعد whenRasterVersionKnown الذي يُحلّ في
    // مايكروتاسك لاحق فقد يسبق استقرار rasterMeta) — يمنع طبقة الرياح من أول رسم بمصدر/مدى
    // خاطئَين (hasWindSpeed=false افتراضياً) قبل أن يصل meta.json فعلياً.
    const [metaSettled, setMetaSettled] = useState(false);
    // الشريط الزمني تمّت مزامنته على إطار "الآن" الصحيح المبني على meta. **حرِج ضد سباق
    // التوقيت:** metaSettled وحده لا يكفي — فحين يصل meta تُرسَم الطبقة في نفس دورة الرسم
    // بينما currentTimeIndex لا يزال 0 (تصحيحه يقع في effect المزامنة الذي يعمل *بعد* الرسم)،
    // فتُحمَّل صورة الإطار 0 (= زمن الدورة، قبل ساعات) لِلحظة ثم تقفز فجأةً إلى الإطار الصحيح
    // = "تتغيّر الألوان بالكامل" و"تختلف عند العودة لنفس التوقيت". نمنع ظهور طبقات الراستر
    // حتى ترتفع هذه الراية داخل effect المزامنة (بعد ضبط الإطار الصحيح)، فلا يُرى إطار خاطئ.
    const [timelineReady, setTimelineReady] = useState(false);

    // نُسج الرياح **بعد** إعلان versionKnown/timelineReady: قراءتهما قبل التعريف تقع في
    // المنطقة الميتة الزمنية (TDZ) فيتعطّل المكوّن بالكامل — كشفه جرد الشبكة فوراً حين
    // هبطت الطلبات من 76 إلى 12 ولم يُطلَب ستايل الخريطة أصلاً.
    // والحارس نفسه ضروري: قبل استقرار الشريط يكون timeIndex = 0، فكانت تُنزَّل ثلاثة
    // ملفّات كاملة تُرمى بعد لحظة (رُصد wind_000/001 قبل wind_005/006/007).
    const windReady = versionKnown && timelineReady;
    // جهوز أوّل إطار راستر (لأي طبقة لونية). مزلاج أحادي الاتجاه: نُبقي قاعدة الخريطة داكنة
    // حتى يرتفع، فتظهر القاعدة الفاتحة (للحرارة/غيرها) والألوان الصحيحة **معاً** بدل وميض
    // القاعدة الفستقية العارية قبل وصول الراستر. يُضبط مرّة واحدة عند أوّل تحميل.
    const [heatmapReady, setHeatmapReady] = useState(false);

    // فحص WebGL مرّة واحدة: بدونه لا خريطة إطلاقاً، فنعرض سبباً مفهوماً بدل سواد صامت.
    const [webglOk] = useState(isWebGLAvailable);

    /**
     * شاشة الافتتاح: تُكشف الخريطة حين يجهز أوّل إطار ملوّن — أو بعد سقف زمني صارم أيّهما
     * أسبق. المطلوب إخفاء ترتيب الظهور المشوّش لا إخفاء التحميل كلّه، وشعارٌ يطول أسوأ
     * من خريطة تصل متأخّرة قليلاً.
     *
     * ── لماذا نزل السقف من 2.4 ثانية إلى 1.2 ─────────────────────────────────
     * السقف يُحسب من **تركيب هذا المكوّن** لا من بداية الصفحة. وقياس المسار الحرج
     * (2026-09-02) أظهر أن التركيب نفسه يقع عند ~2.1 ثانية من الإقلاع (حزمة الخريطة
     * + انتظار إعدادات الأدمن). فكان مجموع ما يراه الزائر من الشعار يقارب **4.5
     * ثانية** — بينما قاعدة الخريطة وتسمياتها تصل قبل ذلك بكثير (‏style.json في
     * ~350 مللي ثانية).
     *
     * أي أن السقف كان يُخفي خريطةً جاهزة خلف شعار. و1.2 ثانية تكفي للغرض الأصلي
     * (ألّا تُرى التسميات معلّقة على سواد)، ولا تحجب ما بعده.
     */
    const [splashExpired, setSplashExpired] = useState(false);
    useEffect(() => {
        const t = window.setTimeout(() => setSplashExpired(true), 1200);
        return () => window.clearTimeout(t);
    }, []);
    useEffect(() => { void whenRasterVersionKnown().then(() => setVersionKnown(true)); }, []);

    /**
     * الزمن **المطلق** المعروض حالياً (epoch بالثواني) — يُحدَّث كل رسم بعد تعريف runEpoch.
     *
     * سببه: المتجر يحفظ **مؤشّر إطار** لا زمناً، والمؤشّر نسبيّ لدورة كل نموذج. GFS يُصدر
     * كل 6 ساعات و ECMWF كل 12، فـ run_epoch يختلف بينهما بساعات. لذلك كان المؤشّر نفسه
     * يعني **زمناً مختلفاً تماماً** بعد تبديل النموذج — يقفز الشريط ساعات رغم أن المستخدم
     * لم يلمسه. هذا ما نصلحه: نثبّت الزمن المطلق لا المؤشّر.
     */
    /** آخر (مجلّد نموذج + مجموعة طبقات) بدأ تخزينها المؤقّت الكامل بالخلفية — منع التكرار. */
    const fullWarmed = useRef<string | null>(null);
    /** آخر ساعة انطلق منها الإحماء القريب — للتمييز بين تقدّم التشغيل والقفزة البعيدة. */
    const lastWarmFrom = useRef<number | null>(null);
    const displayedEpochRef = useRef<number | null>(null);
    /** الزمن المطلق الملتقَط لحظة تبديل النموذج، ليُعاد ربطه بدورة النموذج الجديد. */
    const pinnedEpochRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        // **قبل** مسح meta: نلتقط الزمن المعروض بدورة النموذج **السابق**. المؤثّرات تعمل
        // بترتيب التعريف، وهذا المؤثّر مُعرَّف قبل مؤثّر تحديث displayedEpochRef أدناه،
        // فالقيمة المقروءة هنا هي قيمة الرسم السابق — أي زمن النموذج القديم بالضبط.
        pinnedEpochRef.current = displayedEpochRef.current;

        /*
         * ── خدمة فورية من آخر meta معروف، ثم تحقّق بالخلفية ──────────────────────
         *
         * كان كل تبديل نموذج يمسح meta ويرفع `metaSettled = false`، و`timelineReady`
         * يحجب **كل** الطبقات حتى تصل الاستجابة. ولأن رابط meta مرقّم بدلو دقيقة، فأي
         * تبديل بعد انقضاء الدقيقة يفرض رحلة شبكة كاملة — فيرى المستخدم خريطة عارية
         * طوال زمنها عند كل نقرة.
         *
         * المحفوظ من هذه الجلسة يكفي للعرض الفوري: `run_epoch` يتغيّر مرّات معدودة في
         * اليوم بينما التبديل يقع خلال ثوانٍ من جلب سابق، فالقيمة مطابقة عملياً دائماً.
         * والجلب أدناه يعمل بلا تغيير ويصحّح إن اختلفت — فلا نخسر دقّة، نكسب زمناً.
         */
        const cached = getCachedMeta(rasterDir) as typeof rasterMeta;
        if (cached?.run_epoch) {
            setRasterExt(cached.ext);
            setRasterVersion(cached.run_epoch);
            setRasterMeta(cached);
            setMetaDir(rasterDir);
            setMetaSettled(true);
        } else {
            setRasterMeta(null);
            setMetaDir(null);
            setMetaSettled(false);
        }
        // رابط مرقَّم بدلو دقيقة (metaUrl): هذا الجلب هو ما يحدّد أي دورة تُعرض فعلاً على
        // الخريطة، وقد كان يقرأ نسخة عمرها ساعتان من حافة Cloudflare فتبقى دورة جديدة
        // مخفيّة عن كل الزوّار. الدلو يبقي تبديل النماذج داخل الدقيقة كاش-هت (وهو ما كان
        // no-store يُفسده سابقاً) ويحدّ التقادم بدقيقة واحدة.
        /*
         * ── متابعة الدورة ما دامت الصفحة مفتوحة ─────────────────────────────────
         *
         * كان هذا الجلب يقع **مرّة واحدة** لكل مجلّد نموذج، بلا أي متابعة بعدها. وهذا
         * يكسر الشريط الزمني كسراً صامتاً كلّما نُشرت دورة جديدة والصفحة مفتوحة (أربع
         * مرّات يومياً لـGFS/ICON، مرّتان لـECMWF):
         *
         *   • تسميات الشريط تُحسب من `run_epoch` القديم (المؤشّر i ⇒ الدورة + i ساعة).
         *   • أمّا ملفّات الإطارات فأسماؤها ثابتة (`temperature_012.png`) وقد صار
         *     محتواها للدورة الجديدة.
         *
         * فيُقرأ إطارٌ من دورة، ويُسمّى بزمن دورة أخرى — إزاحة ٦ أو ١٢ ساعة. وهذا هو
         * سبب ما رُصد فعلاً: قيم النهار تظهر تحت تسمية الليل والعكس، وثلاثة أجهزة على
         * نفس المدينة والتوقيت والنموذج تعرض أرقاماً وألواناً مختلفة — لأن كلّ جهاز
         * علِق على الدورة التي كانت سائدة لحظة فتحه، وكلٌّ يملك مزيجاً مختلفاً من
         * إطارات مخزّنة وأخرى مجلوبة حديثاً.
         *
         * فنتابع: كل خمس دقائق، وعند عودة اللسان إلى الواجهة (جهاز نائم ليلاً كاملاً
         * يستيقظ على دورة أحدث بدورتين). والتصحيح ليس تحديث رقم فحسب — كاش العيّنات
         * يُفرَغ أيضاً (`resetForNewRun`) وإلا بقيت قيم الدورة السابقة تُقرأ للتلميح
         * وتسميات المدن والبرق تحت الزمن الجديد.
         */
        const applyMeta = (m: { run_epoch?: number; hours?: number; ext?: string } | null) => {
            if (cancelled || !m?.run_epoch) return false;
            const changed = lastRunEpochRef.current !== null && lastRunEpochRef.current !== m.run_epoch;
            lastRunEpochRef.current = m.run_epoch;
            lastHoursRef.current = m.hours ?? null;
            if (changed) rasterSampler.resetForNewRun();
            return true;
        };

        fetchRasterMeta(rasterDir)
            .then((m: any) => {
                if (cancelled) return;
                applyMeta(m);
                // **قبل setRasterVersion لا بعده.** رفع راية الإصدار يُفرج عن كل الطلبات
                // المنتظرة في whenRasterVersionKnown دفعةً واحدة؛ فلو ضُبط الامتداد بعده
                // لانطلقت الدفعة الأولى بالامتداد القديم — نصف الإطارات png ونصفها webp،
                // وهو أسوأ من الالتزام بأحدهما لأنه يُضاعف ما يُنزَّل ويُفسد كاش الحافة.
                setRasterExt(m?.ext);
                if (m && m.run_epoch) {
                    setCachedMeta(rasterDir, m);     // لتبديل النموذج التالي: عرض فوري
                    setRasterVersion(m.run_epoch);   // ترقيم الروابط بالدورة → كاش يوم كامل صالح
                    setRasterMeta(m);
                    setMetaDir(rasterDir);
                } else if (!getCachedMeta(rasterDir)) {
                    setRasterVersion(0);             // لا meta (تطوير/ICON) → روابط بلا ترقيم
                }
                setMetaSettled(true);
            })
            .catch(() => { setRasterVersion(0); setMetaSettled(true); /* بلا meta: السلوك الافتراضي (إزاحة 0) */ });

        // المتابعة الدورية: نفس المسار أعلاه لكن بلا لمس `metaSettled` (الطبقات معروضة
        // أصلاً، ولا يجوز حجبها لأجل تحقّق دوري).
        const recheck = () => {
            if (document.visibilityState === 'hidden') return;   // لسان مخفيّ: لا شبكة بلا فائدة
            fetchRasterMeta(rasterDir)
                .then((m: any) => {
                    if (cancelled || !m?.run_epoch) return;
                    // دورة جديدة **أو** شريط أطول (نشر جزئي ينمو) — كلاهما يستدعي التحديث.
                    if (m.run_epoch === lastRunEpochRef.current
                        && (m.hours ?? null) === lastHoursRef.current) return;   // لا جديد
                    lastHoursRef.current = m.hours ?? null;
                    applyMeta(m);
                    setCachedMeta(rasterDir, m);
                    setRasterExt(m.ext);
                    setRasterVersion(m.run_epoch);
                    setRasterMeta(m);
                    setMetaDir(rasterDir);
                })
                .catch(() => { /* تعذّر التحقّق: نُبقي الدورة الحالية ونعيد المحاولة لاحقاً */ });
        };
        const timer = window.setInterval(recheck, RUN_RECHECK_MS);
        document.addEventListener('visibilitychange', recheck);
        window.addEventListener('focus', recheck);

        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', recheck);
            window.removeEventListener('focus', recheck);
        };
    }, [rasterDir]);

    // زمن صلاحية الإطار i = زمن الدورة + i ساعة. بلا meta: نبدأ من أقرب ساعة للزمن الحالي
    // (تقريب لا تقليل) كي يكون الإطار 0 = "الآن" تماماً، لا "قبل ساعة" (بداية الساعة الحالية).
    // مدى ترميز قناة R في نسيج الرياح RGB المصغَّر (WIND_SPEED_MAX في السكربتات) — يُمرَّر
    // لشريط ألوان الهيتماب في حالة السقوط إليه كي لا تُفكّ قيمه بمدى VALUE_RANGES (229.5).
    const WIND_RGB_RANGE: [number, number] = [0, 120];

    // راستر سرعة الرياح الرمادي كامل الدقّة (wind-speed_*.png): متى ولّدته الدورة الحالية
    // تقرأ منه طبقة اللون والتلميح بدل قناة R المصغَّرة (المنحرفة عند السواحل حتى ~25 كم).
    const hasWindSpeed = !!rasterMeta?.layers?.includes('wind-speed');
    /*
     * نظير الضغط 16-بت. ليس طبقةً تُعرض للمستخدم بل **قدرة**: خطوط تساوي الضغط تُشتقّ
     * منه، و8 بت على مدى 95 هكتوباسكال = 0.373 لكل مستوى — أي تموّج الخطّ وانزياحه
     * عشرات الكيلومترات في الحقول المسطّحة. الراية من meta.json لا افتراضاً: الدورات
     * التي تسبق توليده موجودة فعلاً، وطلبه فيها 404 لكل ساعة.
     */
    const hasPressure16 = !!rasterMeta?.layers?.includes('pressure16');
    const pressureField = hasPressure16 ? 'pressure16' : 'pressure';
    useEffect(() => { rasterSampler.setWindSpeedAvailable(hasWindSpeed); }, [hasWindSpeed]);

    // جهوزية إطار التشغيل: نتقدّم فقط عندما تكون شبكة الطبقة الفعّالة لذلك الإطار
    // مخزّنة (وإلا نحفّز تحميلها مسبقاً وننتظر) — فلا يظهر إطار مجمّد أثناء التشغيل.
    // **يجب أن يبقى بعد تعريف hasWindSpeed أعلاه** — مصفوفة اعتماديات useCallback تُقيَّم
    // فوراً أثناء الرسم (بخلاف جسم الدالة المؤجَّل)، فقراءة hasWindSpeed هنا قبل تعريفه
    // تُسبّب ReferenceError (TDZ) يُسقط الصفحة بالكامل. كان هذا الترتيب معكوساً سابقاً.
    const playbackCanAdvance = useCallback((idx: number) => {
        if (!mapBounds) return true;
        const type = activeHeatmapType ?? (visibleLayers.wind ? 'wind' : null);
        if (!type) return true;
        // لا نتقدّم إلى ساعة جديدة قبل أن تصبح صورها مفكوكة وجاهزة للـ GPU. بهذا يبقى
        // المزج بين الإطارين متصلاً كالفيديو بدل قفزة زمنية تسبق فيها الجسيمات الألوان.
        if (type === 'wind' || RASTER_TYPES.has(type)) {
            const hour = String(idx).padStart(3, '0');
            const needed: string[] = [];
            if (activeHeatmapType && useRaster) {
                // بانٍ مختلف لا مجلّد مختلف فقط: روابط المحيط تحمل امتداد مانيفستها
                // و`generated_epoch` بدل `run_epoch`. قياس رابط لم يُطلب قطّ يعني
                // «غير جاهز» أبداً — أي شريطٌ يقف ولا يتقدّم.
                needed.push(layerFrameUrl(rasterDir, activeHeatmapType, idx));
            }
            // إطارات الرياح مفهرسة بساعة النموذج، و`idx` على محور المحيط فهرس **يوم** —
            // فطلبها هنا يبني روابط لساعات لا وجود لها. الطبقة بلا جسيمات أصلاً.
            if (!isOceanLayer && visibleLayers.wind) needed.push(rasterUrl(rasterDir, `${hasWindSpeed ? 'wind-speed' : 'wind'}_${hour}.png`));
            if (!isOceanLayer && particlesOn) needed.push(rasterUrl(rasterDir, `wind_${hour}.png`));
            // الخطوط شرطٌ للتقدّم كالألوان تماماً. بدونه يقفز الشريط إلى ساعةٍ ألوانُها
            // جاهزة وخطوطُها لم تصل، فيُرى الاثنان طبقتين منفصلتين تتسابقان — وهو ما
            // يُشتكى منه. ومع الإحماء أعلاه لا ينتظر هذا الشرط شيئاً في الغالب.
            if (!isOceanLayer && isobarsEnabled) needed.push(rasterUrl(rasterDir, `${pressureField}_${hour}.png`));
            /* المعروف أنه غير موجود يُستثنى من الانتظار. `isRasterImageReady` يبقى false
               لملفٍّ ردّ عليه الخادم 404، فانتظارُه انتظارٌ لا ينتهي — يتجمّد التشغيل عند
               تلك الساعة إلى الأبد. الكاش يحسم الغياب بلا رحلة شبكة (‏isRasterMissing)،
               فنتخطّاه ونمضي. يخصّ هذا كل المدخلات لا الضغط وحده. */
            const missing = needed.filter((src) => !isRasterImageReady(src) && !isRasterMissing(src));
            if (!missing.length) return true;
            for (const src of missing) void prefetchRasterImage(src).catch(() => undefined);
            return false;
        }
        const bounds = getStableGridBounds(mapBounds);
        if (weatherGridService.getCachedGrid(type, bounds, effectiveModel, idx, 6)) return true;
        weatherGridService.prefetchGrid(type, bounds, effectiveModel, idx, 6);
        return false;
    }, [mapBounds, activeHeatmapType, useRaster, visibleLayers.wind, particlesOn, hasWindSpeed, rasterDir, effectiveModel, isOceanLayer, oceanUrl, isobarsEnabled, pressureField]);

    /**
     * تباعد **مصدر** البيانات الحقيقي بالساعات، ومنه: هل يجوز المزج الزمني أصلاً؟
     *
     * ECMWF open data يُنشر كل 3 ساعات (وICON كذلك بعد الساعة hourly_through — راجع
     * available_step في icon_to_raster.py)، ونحن نُسقطه على شريط ساعي — فعدّة إطارات
     * متتالية متطابقة ثم يقفز الحقل 3 ساعات دفعةً واحدة. مزج إطارين متباعدين 3 ساعات
     * كأنهما ساعة واحدة **حقيقة مقيسة لا نظرية**: على إعصار يتحرّك 25 كم/س، مركز العين
     * قرأ 28 كم/س بلا مزج (أخضر) و**74 مع المزج** (ماجنتا) — الرياح حول دوّامة ضيّقة
     * تحديداً هي ما يُنتج هذا العيب، فحقل الرياح يبقى خطوةً لا انسياباً حين يتجاوز
     * تباعد المصدر ساعة.
     *
     * لكن حقول الألوان (حرارة/رطوبة/ضغط/سحب/أمطار) منتشرة مكانياً لا دوّامة ضيّقة فيها،
     * فمزجها زمنياً آمن **حتى مع تباعد 3 ساعات** — وهو ما تفعله كل تطبيقات الطقس تقريباً
     * (Windy بينها) لتبدو سلسة رغم تباعد بيانات المصدر.
     *
     * ── لماذا رُفع القيد عن **لون** الرياح أيضاً ─────────────────────────────────────
     *
     * كان `canBlendWind` يُصفّر مزج طبقة لون الرياح على ECMWF كلياً، فتتبدّل ألوانها
     * قفزةً واحدة كل ثلاث ساعات — «صور تتغيّر» لا حركة. ثلاث حقائق مقيسة تقلب الميزان:
     *
     *  ١. **الأرقام المعروضة لا تُمزج أصلاً.** التلميح وتسميات المدن تقرأ من
     *     `rasterSampler.sampleScalar(type, idx, …)` وهي تجلب إطار الساعة الصحيحة
     *     بالفهرس الصحيح بلا أي استيفاء زمني (لا وجود لكسر الإطار في ذلك الملف). فالمزج
     *     على الـGPU يمسّ البكسلات وحدها ولا يمكنه أن يُظهر للمستخدم رقماً مخترَعاً.
     *     قياس «28 مقابل 74» يصف بكسلاً في الصورة، لا قيمةً تُقرأ.
     *
     *  ٢. **الجسيمات تمزج متجهات الرياح نفسها بالفعل** على ECMWF: تتلقّى
     *     `frameFraction={windHour.fraction}` كاملاً مع شبكات prev/cur/next. أي أن
     *     مبدأ «لا تستوفِ رياحاً متباعدة ٣ ساعات» منقوضٌ أصلاً عند المستهلك الأحسّ له،
     *     وإبقاء طبقة اللون وحدها صارمة لا يشتري صحّةً — يشتري قفزةً فقط.
     *
     *  ٣. العيب محصور في **دوّامات ضيّقة سريعة**: عند حافة عين إعصار يعطي المزج متوسط
     *     «العين» و«جدار العين». أمّا بقيّة الخريطة — وهي الغالبية الساحقة ممّا يُشاهَد —
     *     فحقلها متدرّج والمزج فيه دقيق.
     *
     * فالمقايضة الحقيقية: قفزة تعمّ الخريطة كلها كل ثلاث ساعات، مقابل ضبابة عابرة حول
     * دوّامات قليلة. الخيار الثاني أصحّ تجربةً وأصدق تمثيلاً للحركة.
     *
     * للعودة إلى السلوك الصارم: أعِد `windBlendAmount` إلى `canBlendWind ? … : 0`
     * وأعِد `liveBlend={canBlendWind}` في طبقة `weather-heatmap-wind`.
     *
     * القيم من meta.json حين تتوفّر؛ وحتى تُعاد دورة التوليد نستنتجها من النموذج كي يسري
     * الإصلاح فوراً بلا انتظار.
     */
    const sourceStepHours = rasterMeta?.step_hours ?? (effectiveModel === 'ECMWF' ? 3 : 1);
    // خطوة النموذج تحكم حساب تراكم الهطول: ECMWF خطوته 3 ساعات، فجمع ثلاثة إطارات
    // بلا ضرب في Δt كان يعرض ثلث القيمة الحقيقية. راجع accumWindow في rasterSampler.
    useEffect(() => { rasterSampler.setStepHours(sourceStepHours); }, [sourceStepHours]);
    const hourlyThrough = rasterMeta?.hourly_through ?? (sourceStepHours <= 1 ? Infinity : 0);
    /** تباعد المصدر ساعة أو أقل — يبقى معلوماً لمن يحتاجه، ولم يعد يحجب مزج اللون. */
    const windSourceIsHourly = sourceStepHours <= 1 && currentTimeIndex < hourlyThrough;
    void windSourceIsHourly;
    const windBlendAmount = frameFraction;
    const colorBlendAmount = frameFraction;

    const runEpoch = rasterMeta?.run_epoch ?? Math.round(Date.now() / 3_600_000) * 3600;
    const frameHours = rasterMeta?.hours ?? 25;
    const forecastTimes = useMemo(
        () => Array.from({ length: frameHours }, (_, i) => runEpoch + i * 3600),
        [runEpoch, frameHours],
    );
    /*
     * محور الشريط الزمني يتبع الطبقة الفعّالة — منطق Zoom Earth بالضبط:
     *   • طبقات النماذج (GFS/ECMWF/ICON): محور **توقّعات** بالساعات من دورة النموذج.
     *   • القمر الصناعي: **رصد مباشر**، لا نموذج له ولا مستقبل. محوره هو أزمنة الصور
     *     الفعلية (الساعات القليلة الماضية) بخطوة السلسلة نفسها.
     *
     * بلا هذا التبديل يقع مؤشّر الشريط دائماً بعد آخر صورة متاحة (المحور يمتدّ 120 ساعة
     * في المستقبل بينما الصور ماضٍ فقط)، فتبقى الصورة مثبَّتة على آخر إطار ولا يُحرّكها
     * التشغيل — وهو ما كان يحدث.
     */
    const satelliteMeta = useSatelliteFrames(visibleLayers.satellite);
    const satelliteTimes = useMemo(
        () => satelliteMeta?.frames.map((f) => f.epoch) ?? [],
        [satelliteMeta],
    );
    const satelliteAxis = visibleLayers.satellite && satelliteTimes.length >= 2;

    // ساعة الحائط (تُحدَّث عند رأس كل دقيقة). تُعرَّف هنا لا بعد المحور: محور القمر صار
    // يمتدّ إليها، وترتيب التعريف في JS هو ترتيب التنفيذ.
    const [clockEpoch, setClockEpoch] = useState(() => Date.now());
    useEffect(() => {
        let interval = 0;
        const update = () => setClockEpoch(Date.now());
        const timeout = window.setTimeout(() => {
            update();
            interval = window.setInterval(update, 60_000);
        }, 60_000 - (Date.now() % 60_000));
        return () => {
            window.clearTimeout(timeout);
            if (interval) window.clearInterval(interval);
        };
    }, []);

    /**
     * محور القمر يمتدّ إلى **اللحظة الحالية** لا إلى آخر صورة.
     *
     * صور الأقمار تتأخّر 25-55 دقيقة عن الآن، وكان المحور ينتهي عند أحدثها — فيفتح
     * الشريط على زمن ماضٍ ويعرض تسميته («1:00 مساءً» والساعة 1:47)، خلافاً لبقيّة
     * الطبقات التي يقع مؤشّرها على اللحظة الحقيقية. نُلحق علامة واحدة عند الآن، فيبدأ
     * المؤشّر عليها ويقرأ الزائر الدقيقة الحالية.
     *
     * الصورة المعروضة لا تتغيّر بذلك: SatelliteTileLayer يقصّ أي لحظة بعد آخر إطار إلى
     * ذلك الإطار، فيبقى المعروض أحدث صورة موجودة فعلاً — وهو الصحيح. المقطع الأخير من
     * الشريط يعني «لا صورة أحدث بعد»، لا صورةً مفقودة.
     *
     * شرط الدقيقة يمنع مقطعاً بطول صفر حين تصل صورة جديدة لتوّها.
     */
    const satelliteAxisTimes = useMemo(() => {
        if (!satelliteTimes.length) return satelliteTimes;
        const now = Math.floor(clockEpoch / 1000);
        const last = satelliteTimes[satelliteTimes.length - 1];
        // …لكن فقط ما دام التأخّر ضمن المعتاد (≤ 75 د). إن تعطّل المولّد أو تأخّر، كان
        // المؤشّر يقف على «7:25 م» فوق صورة من 3:30 م — كذبٌ صريح في الوقت (مرصود
        // 2026-09-25). عندها ينتهي المحور عند آخر صورة فتقرأ التسمية زمنها الحقيقي.
        return now > last + 60 && now - last <= 75 * 60 ? [...satelliteTimes, now] : satelliteTimes;
    }, [satelliteTimes, clockEpoch]);
    /*
     * محور المحيط — **أيّام رصد ماضية**، ثالث محور في التطبيق بعد التوقّعات والقمر.
     *
     * هنا يقع أبسط جزء وأخطره معاً: على هذا المحور **فهرس الشريط = فهرس الإطار مباشرة**
     * (‏frames[i] هو `sst-anomaly_00i.png`)، بخلاف محور القمر الذي يقيس خطوات 30 دقيقة
     * ولا علاقة لفهرسه بأرقام ملفّات النموذج. هذا التطابق هو ما يجعل كل آلة الإحماء
     * والجاهزية والمزج تعمل هنا كما تعمل على محور التوقّعات بلا أي ترجمة.
     *
     * ولا نُلحق «الآن» بطرفه كما نفعل مع القمر: أحدث ملفّ OISST متأخّر يومين، فعلامةٌ
     * عند اللحظة الحالية كانت ستعني مقطعاً بيومين بلا بيانات يقف المؤشّر في وسطه.
     * آخر علامة هنا = آخر يوم مرصود فعلاً، وهو الصدق.
     */
    const oceanTimes = useMemo(
        () => oceanMeta?.frames.map((f) => f.epoch) ?? [],
        [oceanMeta],
    );
    const oceanAxis = visibleLayers['sst-anomaly'] && oceanTimes.length >= 2;
    const oceanFrameCount = oceanTimes.length;

    /*
     * محور الرادار — **رابع محور**، وأقصرها مدىً وأدقّها خطوةً.
     *
     * كانت للرادار حلقةُ تحريك خاصّة به لا يمسّها الشريط، فيسحبه المستخدم ولا يتحرّك
     * شيء — وهو ما رصده المستخدم بالضبط. والسبب الأصلي وجيه: ربطُ فهرس الشريط بفهرس
     * إطارات الرادار يُطابق ساعةً قادمة بدقيقتين ماضيتين، فيقرأ المشاهد زمناً خاطئاً
     * بثقة. لكن العلاج كان بإلغاء الشريط لا بتصحيحه.
     *
     * والتصحيح هو ما يفعله التطبيق أصلاً مع القمر والمحيط: يُبدَّل **المحور** نفسه.
     * فتصير العلامات أوقات الرصد الحقيقية (‏~24 دقيقة ماضية بخطوة دقيقتين)، ويسحب
     * المستخدم فيرى الإطار الموافق لتلك اللحظة فعلاً، ويتولّى التشغيلَ `useAutoplay`
     * الواحد بدل مؤقّتين يتسابقان على نفس الطبقة.
     *
     * وشرط الاثنين لا الواحد: إطارٌ وحيد يعني مقطعاً بطول صفر يقسم عليه `displayedEpoch`.
     */
    const radarAxis = visibleLayers.radar && radarTimes.length >= 2;

    // الأولوية للرادار: هو الطبقة الوحيدة التي يفتحها المستخدم ليسأل «ماذا يمطر
    // الآن؟» — سؤالٌ لا يجيب عنه محور توقّعٍ يمتدّ خمسة أيام.
    /*
     * محور الرادار رصدٌ، ثم **ساعةُ تنبّؤٍ من الرصد نفسه** بعده.
     *
     * جُرّب امتدادٌ ستّ ساعات إلى توقّع **النموذج** وطلب العميل إزالته في 2026-09-19:
     * «لا أريد رصداً متوقّعاً، أريد الرصد الحالي». ثم طلب في 2026-09-21 (فيديو المقارنة
     * بـWindy) الساعة القادمة بعد آخر رصد مباشرةً. والفرق أن هذه دفعٌ للرصد مع حركته
     * (radar_nowcast.py) بالألوان والنعومة نفسها، لساعة واحدة، ومعلَّمة على الشريط
     * (‏forecastFrom) — و«الآن» يبقى آخر رصد (‏radarObserved).
     */
    const timelineTimes = radarAxis ? radarTimes
        : oceanAxis ? oceanTimes : satelliteAxis ? satelliteAxisTimes : forecastTimes;

    /*
     * ── حرس: الموضع لا يتجاوز طول المحور ────────────────────────────────────────
     *
     * `meta.json` يُعاد جلبه كل دقيقة، و`hours` فيه **قد ينقص**: دورةٌ جديدة تبدأ
     * التوليد فتُعلن ما اكتمل منها فقط (‏40 ساعة مثلاً) قبل أن تبلغ 121. وحين يقع ذلك
     * والمستخدم واقف عند الساعة 130، يبقى `currentTimeIndex` حيث هو بينما اختفت
     * الملفّات من تحته — فينهال سيل 404 على الساعات 125-133، وهو ما رُصد حرفياً.
     *
     * ونفس الشيء يقع عند إطفاء طبقة القمر الصناعي: محورها قد يكون أطول من محور
     * التوقّعات، فالعودة إليه تترك الموضع خارج المدى.
     *
     * كان الحرس موجوداً على `currentTimeIndex + 1` وحده (‏`Math.min(..., frameHours - 1)`
     * في `urlNext`) — أي أن الإطار **التالي** محروس والإطار **المعروض** لا. نقصٌ صامت:
     * يُخفي نصف العَرَض فيبدو الخلل عشوائياً.
     *
     * نُصحّح الموضع نفسه لا الرابط: قصُّ الرابط وحده يعني خريطةً تعرض الساعة 120 بينما
     * يقول الشريط 130 — كذبٌ أسوأ من 404.
     */
    useEffect(() => {
        const last = timelineTimes.length - 1;
        if (last < 0 || currentTimeIndex <= last) return;
        setCurrentTimeIndex(last);
        setFrameFraction(0);
    }, [timelineTimes.length, currentTimeIndex, setCurrentTimeIndex, setFrameFraction]);

    // الزمن المطلق المعروض على الشريط — تتبعه طبقة القمر الصناعي لاختيار إطارها.
    // يُحسب من المحور الفعّال (لا من runEpoch) كي يصحّ للمحورين معاً.
    const displayedEpoch = useMemo(() => {
        const i = Math.max(0, Math.min(timelineTimes.length - 1, currentTimeIndex));
        const a = Number(timelineTimes[i] ?? runEpoch);
        const b = Number(timelineTimes[Math.min(i + 1, timelineTimes.length - 1)] ?? a);
        return a + (b - a) * frameFraction;
    }, [timelineTimes, currentTimeIndex, frameFraction, runEpoch]);
    // يتتبّع الزمن المطلق المعروض. **يجب أن يبقى بعد تعريف runEpoch وقبل مؤثّر المزامنة**:
    // ترتيب التعريف هو ترتيب التنفيذ، وعليه يعتمد التقاط زمن النموذج القديم أعلاه.
    useEffect(() => {
        displayedEpochRef.current = displayedEpoch;
    }, [displayedEpoch]);

    /**
     * موضع ساعة الرياح لجسيمات الرياح — **ليس بالضرورة currentTimeIndex**.
     *
     * نُسج الرياح مفهرسة بساعة النموذج (wind_000..wind_120 من دورة النموذج)، بينما
     * currentTimeIndex موضعٌ على المحور الفعّال. على محور التوقّعات هما الشيء نفسه، لكن
     * على محور القمر الصناعي يصير currentTimeIndex فهرساً في سلسلة صور خطوتها 30 دقيقة —
     * فتعرض الجسيمات رياح الساعات 0..24 من دورة النموذج بدل رياح اللحظة المعروضة فعلاً،
     * أي حقلاً لا علاقة له بالصورة تحته.
     *
     * نشتقّ الساعة من الزمن المطلق المعروض، فتتطابق الجسيمات مع صورة القمر على أي محور.
     */
    const windHour = useMemo(() => {
        // على محور المحيط: currentTimeIndex فهرس **يوم**، وتمريره كساعة نموذج يطلب
        // إطارات رياح لا وجود لها (‏60 يوماً > عدد ساعات أي دورة) — سيل 404. ولا يصحّ
        // اشتقاقها من الزمن المعروض أيضاً: أيّام ماضية تقع كلّها قبل دورة النموذج.
        // نُثبّت الساعة صفراً؛ الجسيمات مطفأة على هذه الطبقة أصلاً (defaultParticles=false).
        if (oceanAxis) return { index: 0, fraction: 0 };
        // ومحور الرادار مثل محور القمر حرفياً: `currentTimeIndex` عليه فهرسُ إطارٍ
        // خطوتُه **دقيقتان**، فتمريره كساعة نموذج يجعل الشريط يتقدّم دقيقتين بينما
        // تقفز التوقّعات ساعةً كاملة. رُصد ذلك على لوحة «توقّعات كل ساعة» فعلاً.
        if (!(satelliteAxis || radarAxis) || !rasterMeta) return { index: currentTimeIndex, fraction: frameFraction };
        const h = (displayedEpoch - rasterMeta.run_epoch) / 3600;
        const clamped = Math.max(0, Math.min(rasterMeta.hours - 1, h));
        const index = Math.floor(clamped);
        return { index, fraction: index >= rasterMeta.hours - 1 ? 0 : clamped - index };
    }, [oceanAxis, satelliteAxis, radarAxis, rasterMeta, displayedEpoch, currentTimeIndex, frameFraction]);
    /**
     * ساعة النموذج لطبقة الألوان — **نفس علّة الجسيمات أعلاه، وقد فاتت عندها**.
     *
     * كان رابط النسيج الملوَّن يُبنى من `currentTimeIndex` مباشرةً. وهو ساعةُ نموذجٍ على
     * محور التوقّعات وحده؛ أمّا على محور الرادار فهو فهرسٌ في شبكة خمس دقائق، وعلى محور
     * القمر فهرسٌ في سلسلة نصف ساعية. فمع إشعال الرادار تصير الألوان ساعاتِ النموذج
     * 0..23 من بداية الدورة — حقلٌ لا علاقة له باللحظة المكتوبة تحت المؤشّر، ولا يتحرّك
     * مع الرادار الذي يُفترض أن يرافقه. وهذا ما بلّغ عنه العميل.
     *
     * والمفارقة أن العلاج موجودٌ منذ إصلاح الجسيمات (`windHour` فوق) — طُبّق على
     * الجسيمات ونُسي على الألوان، وهما يقرآن من نفس الدورة.
     *
     * طبقة المحيطات تبقى على `currentTimeIndex`: محورها **أيّام** لا ساعات نموذج،
     * وإطاراتها مفهرسة به فعلاً.
     */
    const heatHour = isOceanLayer
        ? { index: Math.min(currentTimeIndex, oceanFrameCount - 1), fraction: colorBlendAmount }
        : windHour;

    // الساعة المعروضة تسبق جارَيها في النطاق دائماً (راجع بوّابة useWindRaster): بلا ذلك
    // تتقاسم ثلاثةُ ملفّات نطاقَ القفزة الواحدة، فتلحق الجسيمات بالألوان متأخّرةً.
    const windGridPrev = useWindRaster(Math.max(0, windHour.index - 1), rasterDir, windReady, 'neighbour');
    const windGridCur = useWindRaster(windHour.index, rasterDir, windReady, 'now');
    const windGridNext = useWindRaster(windHour.index + 1, rasterDir, windReady, 'neighbour');

    // إطار "الآن" = إزاحة الساعات بين الزمن الحالي وزمن دورة النموذج (f000). يُستخدم لفتح
    // الشريط على الآن (لا على بداية الدورة في الماضي) ولإعادة التشغيل إليه عند النهاية.
    const nowFramePosition = useMemo(() => {
        // على محور القمر «الآن» = آخر علامة في المحور، وهي اللحظة الحالية نفسها بعد أن
        // صار المحور يمتدّ إليها (satelliteAxisTimes) — فيفتح الشريط على الدقيقة الحالية
        // كبقيّة الطبقات، والصورة المعروضة تبقى أحدث ما هو متاح فعلاً.
        // على محور المحيط «الآن» = آخر يوم مرصود (لا اللحظة الحالية: أحدث رصد متأخّر يومين).
        // على محور الرادار «الآن» = آخر إطار مرصود، لا اللحظة الحالية: أحدث مركَّب
        // متأخّر دقيقتين، فعلامةٌ عند الآن كانت ستعني مقطعاً بلا بيانات يقف المؤشّر فيه.
        // وبعد إضافة تنبّؤ الساعة القادمة: آخر **رصد**، لا آخر علامة — الشريط يفتح على
        // الحاضر، والتنبّؤ أمامه لمن يُشغّل أو يسحب.
        if (radarAxis) return { index: Math.max(0, radarObserved - 1), fraction: 0 };
        if (oceanAxis) return { index: oceanFrameCount - 1, fraction: 0 };
        if (satelliteAxis) return { index: satelliteAxisTimes.length - 1, fraction: 0 };
        if (!rasterMeta) return { index: 0, fraction: 0 };
        const position = Math.max(0, Math.min(rasterMeta.hours - 1,
            (clockEpoch / 1000 - rasterMeta.run_epoch) / 3600));
        const index = Math.floor(position);
        return { index, fraction: index >= rasterMeta.hours - 1 ? 0 : position - index };
    }, [radarAxis, radarObserved, oceanAxis, oceanFrameCount, satelliteAxis, satelliteAxisTimes.length, rasterMeta, clockEpoch]);
    const nowFrameIndex = nowFramePosition.index;

    // نُعيد ضبط الشريط على إطار "الآن" عند كل تبديل نموذج (تغيّر rasterDir) وعند وصول meta
    // الجديد. مهمّ أن يعمل حتى بلا meta: في حالة السقوط (runEpoch = الساعة الحالية) يكون
    // nowFrameIndex = 0 = الآن، فلا يبقى المؤشّر على إزاحة النموذج السابق (مثل +7 ساعات).
    useEffect(() => {
        // نضبط الإطار الصحيح أولاً (zustand يُحدِّث المتجر متزامناً)، ثم نرفع الراية — فأي
        // إعادة رسم تنشأ عن رفعها تقرأ currentTimeIndex الصحيح فوراً (لا الإطار 0 القديم).
        if (timelineAutoSync) {
            setCurrentTimeIndex(nowFrameIndex);
            setFrameFraction(nowFramePosition.fraction);
        } else if (rasterMeta && metaDir === rasterDir && pinnedEpochRef.current != null) {
            // المستخدم اختار توقيتاً بنفسه (timelineAutoSync = false)، وبدّل النموذج.
            // نُعيد ربط **نفس اللحظة الزمنية** بدورة النموذج الجديد بدل إبقاء المؤشّر
            // كما هو — فالمؤشّر نفسه يعني زمناً آخر تحت دورة أخرى.
            const position = Math.max(0, Math.min(rasterMeta.hours - 1,
                (pinnedEpochRef.current - rasterMeta.run_epoch) / 3600));
            const index = Math.floor(position);
            setCurrentTimeIndex(index);
            setFrameFraction(index >= rasterMeta.hours - 1 ? 0 : position - index);
            // مرّة واحدة لكل تبديل: وإلّا أعادت أي إعادة تشغيل لاحقة للمؤثّر (تحرّك الساعة
            // كل دقيقة) ربطَ الزمن القديم فأبطلت تحريك المستخدم للشريط بعد التبديل.
            pinnedEpochRef.current = null;
        }
        // نرفعها فقط بعد استقرار meta كي لا تظهر الطبقات على الربط الافتراضي الخاطئ (الإطار
        // 0 = "الآن"). في حالة التطوير/ICON بلا meta يُضبط metaSettled=true أيضاً فتظهر عادياً.
        if (metaSettled) setTimelineReady(true);
    }, [rasterDir, rasterMeta, metaDir, metaSettled, nowFrameIndex, nowFramePosition, timelineAutoSync, setCurrentTimeIndex, setFrameFraction]);

    // عند تبديل المحور (تشغيل/إطفاء القمر) يختلف طول الشريط تماماً (10 إطارات مقابل 121
    // ساعة)، فيبقى المؤشّر خارج المدى ما لم يكن التزامن التلقائي مفعّلاً. نُعيده إلى
    // «الآن» على المحور الجديد — وإلا ظهر الشريط عالقاً على طرفه.
    useEffect(() => {
        setCurrentTimeIndex(Math.max(0, nowFramePosition.index));
        setFrameFraction(nowFramePosition.fraction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [satelliteAxis, oceanAxis, oceanFrameCount]);

    /**
     * مزامنة ساعة التشغيل مع المتجر **خارج** التشغيل التلقائي.
     *
     * أثناء التشغيل تكتب useAutoplay الساعةَ مباشرةً بدقّة كاملة. أمّا خارجه — سحب مقبض
     * الشريط، النقر على ساعة، تبديل نموذج يُعيد الربط إلى «الآن» — فالمتجر هو المصدر،
     * ويجب أن تتبعه الساعة وإلّا بقيت الطبقات على آخر نسبة مزج خلّفها التشغيل (مثلاً
     * 0.6 من ساعة لم تعد معروضة) فيظهر الحقل مزاحاً زمنياً بلا سبب ظاهر.
     */
    useEffect(() => {
        // أثناء التشغيل: useAutoplay هي المصدر. وأثناء السحب: TimeSlider يكتب القيمة
        // العشرية الخام بدقّة الإصبع، وهذا المؤثّر يستقبل قيمةً مقرَّبة إلى 10 دقائق —
        // فلو كتبها لأعاد التكميم الذي أُزيل للتوّ ولعاد السحب متقطّعاً.
        if (isPlaying || timelineSeeking) return;
        setPlaybackPosition(currentTimeIndex, frameFraction);
    }, [isPlaying, timelineSeeking, currentTimeIndex, frameFraction]);

    useAutoplay({
        isPlaying,
        playbackSpeed,
        // الرادار: الحلقة على **الرصد وحده** — تقف عند آخر رصد ثم تعود (سلوك Windy،
        // وطلب العميل 2026-09-24). التنبّؤ يبقى على الشريط يُسحب إليه يدوياً.
        frameCount: radarAxis && radarObserved >= 2 ? radarObserved : timelineTimes.length,
        // القمر الصناعي صور جاهزة لا نُسج تُحمَّل حسب الطبقة — لا شرط جهوز يؤجّل التقدّم.
        // المحيط يمرّ بـplaybackCanAdvance كبقيّة طبقات النسيج: إطاراته ملفّات تُنزَّل،
        // فالتقدّم قبل وصولها هو نفس التلعثم الذي يعالجه الحارس على محور التوقّعات.
        // الرادار صور جاهزة كالقمر — لا نُسج تُحمَّل حسب الطبقة، فلا شرط جهوز يؤجّل.
        // الرادار بطبقة WebGL: حارسها يعرف هل وصلت بلاطات الشاشة للإطار التالي
        // (‏radarGLCanAdvance)، ويُرجع true دائماً في مسار الألوان القديم.
        canAdvance: satelliteAxis ? undefined : radarAxis ? radarGLCanAdvance : playbackCanAdvance,
        homeIndex: nowFrameIndex,
        // الخطوات الفرعية = **نعومة** المزج البصري وحدها على الـ GPU (60 FPS).
        // خطوات فرعية أكثر للمحيط: المقطع الواحد **يوم كامل**، والفرق بين يومين متتاليين
        // في حقل الشذوذ صغير ومتّصل — فمزجٌ ناعم عبر 12 خطوة يجعل تطوّر اللسان يبدو
        // تدفّقاً مستمرّاً لا شرائح يومية تقفز.
        // ‏1 للرادار: **لا مزج بين إطاراته**. حقلا الحرارة يتّصلان فيصحّ استيفاؤهما،
        // أمّا الصدى فحدوده حادّة وحركته سريعة، ومزجُ إطارين متباعدين بدقيقتين يُنتج
        // عاصفتين شبحيّتين في موضعين — وهو ما يمنعه `raster-fade-duration: 0` أصلاً في
        // الطبقة. فخطوةٌ فرعية واحدة تُبقي المؤشّر على أوقات رصد حقيقية لا بينها.
        subSteps: radarAxis ? 1 : oceanAxis ? 12 : satelliteAxis ? 10 : 6,
        // زمن عبور الإطار الواحد. 700 ثمّ 1100 بدتا سريعتين — 1400 إيقاع أهدأ يتيح تمييز
        // تفاصيل الغيوم بوضوح (24 إطاراً/12 ساعة في ~34 ثانية) مع بقاء الحركة متّصلة لا
        // شرائح. الجزء الأكبر ممّا بدا "سرعة" في التجربة السابقة كان تقطّعاً من تعثّر
        // شبكي لا إيقاعاً سريعاً فعلاً — عولج ذلك في useAutoplay (خطوة واحدة كحدّ أقصى
        // لكل نبضة) وفي التحميل المسبق (نافذة صغيرة بدل السلسلة كلّها دفعة واحدة).
        /*
         * ‏900 لا 3000. ثلاث ثوانٍ لكل ساعة توقّع تعني عبور الأيام الخمسة في **ست
         * دقائق** — إيقاعٌ لا يكاد المشاهد يلمح فيه تغيّر اللون، وهو ما وُصف بأنه
         * «بطيء جدّاً» مقارنةً بـWindy وZoom Earth.
         *
         * صار ممكناً الآن ولم يكن كذلك: التسريع بلا إحماء كافٍ يعني إطارات ناقصة
         * ووميضاً. وقد سبقته ثلاثة أشياء تجعله آمناً — الساعة المطلوبة في رأس الطابور،
         * ووضع التركيز الذي يمنح النطاق كلّه لما ينتظره العرض، وحارس canAdvance الذي
         * يُبطئ التقدّم بدل تخطّي إطار لم يصل.
         *
         * وقيمة القمر الصناعي (1400) تبقى: خطوتها 30 دقيقة لا ساعة، وقد ضُبطت بالتجربة.
         */
        /*
         * ‏600 مللي لليوم على محور المحيط — أسرع من محور التوقّعات لا أبطأ.
         *
         * الوحدة هنا يوم لا ساعة، والنافذة 60 يوماً. بإيقاع 900 كان العبور يستغرق ~54
         * ثانية: أطول ممّا يصبر عليه مشاهد ينتظر «متى يتشكّل اللسان». و600 تعطي ~36
         * ثانية لشهرين كاملين، وهو إيقاع يُظهر التطوّر كحركة متّصلة — وهذا هو كل الغرض
         * من إعطاء الطبقة محوراً زمنياً بدل صورة واحدة.
         */
        // ‏450 للرادار ≈ إيقاع Zoom Earth: أسرع منه يومض فلا تُقرأ حركة العاصفة، وأبطأ
        // منه يجعل الاثني عشر إطاراً تتجاوز ثمان ثوانٍ للدورة.
        frameMs: radarAxis ? RADAR_STEP_MS : oceanAxis ? 600 : satelliteAxis ? 1400 : 900,
        // Zoom Earth يُكرّر حلقة القمر الصناعي بلا توقّف بدل الوقوف عند آخر إطار.
        // والمحيط كذلك: سلسلة ماضية مغلقة، ومشاهدتها تتكرّر طبيعياً كحلقة.
        // والرادار كذلك: سلسلة ماضية مغلقة، حلقتها هي الطريقة الوحيدة لقراءتها.
        loop: satelliteAxis || oceanAxis || radarAxis,
    });

    // تحميل مُسبق لإطارات النسيج (الطبقة الفعّالة + الرياح) فتظهر فوراً عند التشغيل بلا
    // تأخّر تنزيل. نعتمد كاش المتصفح: مجرّد إنشاء Image يكفي للإحماء.
    useEffect(() => {
        if (!versionKnown) return;
        const types: string[] = [];
        if (useRaster && activeHeatmapType) types.push(activeHeatmapType);
        /*
         * **نسيج ألوان طبقة الرياح — كان غائباً تماماً عن الإحماء.**
         *
         * `activeHeatmapType` يستبعد 'wind' صراحةً (`heatmapTypes.filter(l => l !== 'wind')`)،
         * فحين تكون الرياح هي الطبقة الوحيدة المفتوحة تكون قيمته `null` ولا يُدرَج شيء
         * أعلاه. ثم يُدرَج `'wind'` أدناه — وهو نسيج **المتّجهات** الذي تقرؤه الجسيمات،
         * لا نسيج الألوان. أمّا ما يُرسم لوناً على الشاشة فهو `wind-speed_NNN.png`
         * (راجع خصائص الطبقة: `hasWindSpeed ? 'wind-speed' : 'wind'`).
         *
         * فالنتيجة المرصودة بالضبط: الجسيمات تتحرّك بسلاسة لأن نسيجها مُحمّى، والألوان
         * تتأخّر لأن نسيجها لم يكن يُطلب إلا لحظة بلوغه — ومعها الشريط الأزرق يقيس
         * جاهزية `wind` ويسكت عن `wind-speed`، فيُعلن «جاهز» بينما اللون ما زال ينتظر.
         *
         * نُدرج ما يُرسم فعلاً. والنسيجان مطلوبان معاً هنا: الألوان من هذا، والجسيمات من
         * ذاك — فلا يُغني أحدهما عن الآخر.
         */
        // على محور المحيط الفهرس **يوم** لا ساعة نموذج: إحماء الرياح به يبني روابط
        // لساعات غير موجودة (سيل 404) ويزاحم إطارات المحيط على نطاق الشبكة نفسه.
        if (!isOceanLayer && visibleLayers.wind && hasWindSpeed) types.push('wind-speed');
        // رواستر الرياح يغذّي شبكة الجسيمات فوق **كل** الطبقات (لا طبقة الرياح فقط). فإن
        // اكتفينا بـ visibleLayers.wind، بقيت إطارات الرياح تُحمَّل عند الطلب أثناء تحريك
        // الخط الزمني على طبقة الحرارة/غيرها، فتتأخّر شبكة الجسيمات وتبقى تتبع الحقل القديم
        // حتى تصل الصورة الجديدة (الجسيمات "تنتقل ببطء للموضع الصحيح"). نُحمّلها مسبقاً
        // كلما كانت الجسيمات مفعّلة كي يجهز الإطار التالي قبل بلوغه.
        if (!isOceanLayer && (visibleLayers.wind || particlesOn)) types.push('wind');
        /*
         * **مدخلات الطبقة الفعّالة، لا نسيجها وحده.**
         *
         * كانت النافذة القريبة تُحمّي نسيج الطبقة المعروضة فقط. هذا يكفي الحرارة والرطوبة
         * وأمثالهما (نسيج واحد لكل إطار)، ولذلك بدت الحرارة وحدها سلسة. أمّا الأمطار فتبني
         * إطارها من **ثلاث** ساعات هطول متتالية (تراكم PRECIP_ACCUMULATION_HOURS)، ويقرأ
         * أنيميشن المطر معها نسيجَ الحرارة، ومع تشغيل البرق نسيجَ k-index أيضاً. أيٌّ من
         * هذه المدخلات لم يكن يُحمَّى، فيُطلب لحظة الوصول إليه — وهو سبب كون الأمطار مع
         * البرق أبطأ الطبقات تنقّلاً بفارق واضح.
         *
         * إدراجها هنا يجعل كل طبقة تتعامل كما تتعامل الحرارة: كل ما يلزم الإطار التالي
         * يصل قبل بلوغه.
         */
        // محلّياً لا عبر isPrecipActive: ذاك يُعرَّف بعد هذا المؤثّر، وقراءته في مصفوفة
        // الاعتماديات (تُقيَّم أثناء الرسم) تُسبّب ReferenceError يُسقط الصفحة.
        const precipActive = activeHeatmapType === 'precipitation';
        if (precipActive) {
            types.push('temperature');                       // يقرؤه أنيميشن الهطول
            types.push('k-index');                           // تخفيف البياض فوق الخلايا الرعدية
        }
        /*
         * **راستر الضغط — مدخل الخطوط، وكان غائباً عن الإحماء والقياس معاً.**
         *
         * الخطوط تُرسم من `pressure16_NNN.png` في شيدر مستقلّ، وأرقامها تُشتقّ من الصورة
         * نفسها. لم يكن هذا النسيج مُدرجاً هنا، فترتّب عليه أمران يُريان معاً:
         *
         * 1. لا إحماء له — يُطلب لحظة بلوغ الساعة فعلاً. فبينما ألوان الطبقة جاهزة سلفاً
         *    وتتبدّل فوراً، تنتظر الخطوط تنزيلاً كاملاً عند كل خطوة. هذا هو التفاوت
         *    المرصود: اللون يسبق والخطّ يلحق.
         * 2. الشريط الأزرق يقيس جاهزيّته على هذه القائمة نفسها (‏setReadinessSource أدناه)،
         *    فكان يُعلن الإطار «جاهزاً» وهو لا يعرف بالضغط شيئاً — أزرقُ يَعِد بما لا يملك.
         *
         * إدراجه يُصلح الاثنين بسطرٍ واحد: يدخل نافذةَ الإحماء القريبة وطابورَ الأيام
         * الخلفي، ويدخل القياس فيصدق المؤشّر. والكلفة محصورة بمن فعّل الخطوط فعلاً.
         *
         * `pressureField` هو نفسه الذي تبنيه الطبقة (‏pressure16 متى توفّر وإلّا pressure)،
         * و`layerFrameUrl` يبني الرابط ذاته حرفاً بحرف — فالإحماء يملأ الكاش الذي تقرأ منه
         * الطبقة، لا كاشاً موازياً.
         */
        if (!isOceanLayer && isobarsEnabled) types.push(pressureField);
        // نفس قائمة المدخلات التي نُحمّيها هي التي يقيس عليها الشريط الزمني جاهزيّته —
        // فلا يكذب المؤشّر: ما يُعلَن «جاهز» هو ما لن ينتظر تنزيلاً عند الانتقال إليه.
        // المجلّد وعدد الإطارات يتبعان المحور الفعّال: 60 يوماً في rasters/ocean/ أو
        // ساعات الدورة في مجلّد النموذج. خلطهما يقيس جاهزية ملفّات لا تُطلب أصلاً.
        const warmDir = isOceanLayer ? OCEAN_RASTER_DIR : rasterDir;
        const warmFrames = isOceanLayer ? oceanFrameCount : frameHours;

        /*
         * محور الرادار يقيس جاهزيّته على **إطاراته هو**.
         *
         * كان المؤشّر يبقى على مصدر نُسج التوقّعات، فيرسم جاهزية ملفّاتٍ لا تُطلب على
         * هذا المحور أصلاً — ولأن طول المحور صار عدد إطارات الرادار (اثنا عشر) لا عدد
         * ساعات الدورة، كان الشريط يظهر فارغاً. وهو ما رصده المستخدم.
         *
         * والإطار «جاهز» هنا حين تصل بلاطاته **كلّها**: نطاقٌ واحد ناقص يعني وميضاً
         * عند الانتقال إليه، فالجزئي غير جاهز — نفس قاعدة مسار النُسج حرفياً.
         */
        if (radarAxis && radarManifest) {
            // بالزمن لا بالفهرس — نفس اختيار RadarTileLayer، وإلّا قاس الشريط جهوز
            // إطاراتٍ غير التي تُعرض (المحور شبكة 5 د، والأرشيف كل دقيقتين لـMRMS).
            // **وعلى نطاقاته المرئية وحدها.** الجاهزية هنا هي ما ينتظره التشغيل قبل أن
            // يتقدّم خطوة؛ فإدراج نطاقات خارج الشاشة يجعل الحلقة تنتظر ميغابايتات لا
            // تُرسَم — وهو سبب زحف الحلقة المقيس في 2026-09-19 (راجع domainInView).
            // الفسيفساء نطاقٌ واحد يغطّي العالم؛ وبدونها نقصّ على المرئي كما كان.
            const seen = radarMosaic
                ? [radarMosaic]
                : radarManifest.domains.filter((d) => domainInView(d.bounds, mapBounds));
            const frameUrls: string[][] = radarTimes.map((t) => {
                const urls: string[] = [];
                for (const d of seen) {
                    const f = frameAtEpoch(d, t);
                    // **ما يُرسم فعلاً**: بلاطات الشاشة للنطاق المقطَّع، والمصغَّرة لما
                    // لم يُقطَّع بعد. قياس المصغَّرة بعد التقطيع كان يُنزّل ما لا يُعرض —
                    // عشرات الميغابايتات تُشبع الشبكة فتتضوّر ساعة التشغيل، وهو ما قِيس
                    // في 2026-09-20: خطوة كل 2.1 ثانية بدل 450 مللي، وشريطٌ بلا إحماء.
                    // الجاهزية بالمستوى **الأخشن بدرجة** لا بالمعروض: التكبير بدرجة يجعل
                    // الأخشن الجديد هو الدقيق القديم — في الكاش أصلاً — فلا يفرغ الشريط
                    // ولا تتوقّف الحركة، والدقيق يُسخَّن خلفه ويتّضح خلال لحظة. وهذا ما
                    // يُرى في Zoom Earth: الحركة تستمرّ عبر التكبير، ولا تنتظره. قبله
                    // كان كل تكبير يُفرّغ الشريط ويوقف التشغيل (رُصد في 2026-09-21).
                    if (f) urls.push(...radarViewTileUrls(d, f, mapBounds, Math.max(0, zoomLevel - 1)));
                }
                return urls;
            });
            setReadinessSource({ dir: 'radar', types: [], frames: radarTimes.length, frameUrls });
        } else {
            setReadinessSource(satelliteAxis || types.length === 0 || warmFrames <= 0 ? null : {
                dir: warmDir,
                types: [...types],
                frames: warmFrames,
            });
        }

        // ترتيب الإحماء = ترتيب التشغيل: للأمام من الإطار الحالي أولاً (وهو ما سيُشغَّل
        // فعلاً) ثم القليل خلفه. الطابور ذو تزامن محدود يُكمل الإطارات **بالترتيب** فيصبح
        // الإطار التالي جاهزاً قبل البعيد — عكس إطلاق 25 دفعةً تنتهي كلها متأخّرةً معاً.
        // الإطار الحالي نفسه تُحمّله الطبقة مباشرةً (texImage2D)، فنستبعده كي لا ينافسها.
        const order: number[] = [];
        // نافذة تشغيل قصيرة تكفي عدة ثوانٍ من الـ timeline. تنزيل جميع الإطارات (حتى
        // 120× طبقة) يزاحم الإطار المطلوب فعلاً ويجعل تبديل النماذج أبطأ.
        // نافذة الإحماء تتبع **نيّة المستخدم**، لا وقتاً ثابتاً:
        //   • قبل ظهور أوّل إطار: إطار واحد. كل راستر يُفكّ إلى 4 ميغابايت بكسلات، فخمسة
        //     إطارات × نوعين (~40 م.ب) تزاحم الإطار الوحيد الذي ينتظره المستخدم.
        //   • بعد ظهوره وقبل التشغيل: إطاران — يكفيان لخطوة أو خطوتين يدويّتين.
        //   • **أثناء التشغيل: سبعة**. هنا المطلوب حركة متّصلة كالفيديو، والتلعثم يأتي من
        //     انتظار ملفّ لم يصل؛ سبعة إطارات مقدّماً تُبقي الشريط سابقاً للعرض دائماً.
        //     الكلفة مقبولة لأنها تقع بعد أن يكون المستخدم قد رأى خريطته وطلب التشغيل.
        // نافذة قريبة أوسع من ذي قبل (كانت 7 حدّاً أثناء التشغيل): تُبقي عدّة دقائق من
        // التشغيل سابقة للعرض دائماً، لا ثوانٍ معدودة فقط.
        // إطاران خارج التشغيل كانا سبباً مباشراً لاختفاء الألوان **عند السحب**: يكفي أن
        // يجرّ المستخدم الشريط ثلاث ساعات ليخرج من النافذة المفكوكة، فينتظر فكّاً جديداً.
        // ثمانية إطاران في الاتجاهين تغطّي سحبة إصبع كاملة على الشاشات الصغيرة، وكلفتها
        // مقبولة الآن لأن كاش الفكّ (‏40) لم يعد تُزاحمه التعبئة الخلفية (صارت بايتات فقط).
        const lookAhead = isPlaying
            ? Math.min(16, warmFrames - 1)
            : heatmapReady ? Math.min(8, warmFrames - 1) : Math.min(1, warmFrames - 1);
        // نقطة الانطلاق = ساعة النموذج المعروضة فعلاً، لا موضع المحور. على محور القمر
        // الصناعي يقيس المحور خطوات 30 دقيقة، فكان الإحماء يجلب ساعات نموذج لا علاقة لها
        // بما يُعرض — أي يُنزّل ملفّات لا تُستخدم ويترك ما يُستخدم غير محمَّل.
        // على محور المحيط فهرس الشريط **هو** رقم الإطار (‏frames[i] = sst-anomaly_00i)،
        // فنُحمّي حول ما يُعرض فعلاً. windHour مثبّتة على 0 هناك ولا تصلح مرجعاً.
        const warmFrom = isOceanLayer ? currentTimeIndex : windHour.index;

        /*
         * **قفزة بعيدة ⇒ أفرغ الصفّ العاجل قبل إدراج النافذة الجديدة.**
         *
         * التشغيل يتقدّم ساعةً ساعة فتتداخل النوافذ ويبقى أغلب ما في الصفّ نافعاً. أمّا
         * سحب المؤشّر من الساعة 20 إلى 70 فيجعل كل ما فيه (حتى 32 مهمّة من الموضع القديم)
         * بلا قيمة — لكنه يحتلّ فتحات التزامن الستّ، فيقف خلفه الإطارُ الذي يحدّق فيه
         * المستخدم الآن. وهذا حرفياً ما وُصف: «أحرّك المؤشّر لقدّام فتتوقّف البيانات وتبدأ
         * تحمّل، بعدها تشتغل».
         *
         * الحدّ = طول النافذة نفسها: أي انتقال يتجاوزها يعني أن لا تقاطع بين القديم
         * والجديد أصلاً. والتعبئة الخلفية لا تُمسّ (تُدرَج مرّة واحدة ولا تُعاد).
         */
        /*
         * **أي انتقال ⇒ أفرغ الصفّ العاجل.** كان الشرط `|Δ| > lookAhead` فيغطّي القفزة
         * البعيدة وحدها. لكن سحبةً بخمس ساعات تُبقي حتى 32 مهمّة من الموضع القديم أمام
         * الساعة التي ضغط عليها المستخدم للتوّ — فتنتظر خلف ملفّات تجاوزها أصلاً.
         *
         * الإفراغ لا يفقد شيئاً: النافذة الجديدة تُدرَج في نفس النبضة وبرأسها الساعة
         * المقصودة، وحرّاس `isRasterBytesReady`/`isRasterImagePending` يمنعون إعادة
         * تنزيل ما وصل. أي أن كلفته إعادة بناء مصفوفة صغيرة، ومكسبه أن الساعة المطلوبة
         * تتصدّر الطابور **دائماً** لا في القفزات البعيدة فقط.
         *
         * التعبئة الخلفية لا تُمسّ (تُدرَج مرّة واحدة بحارس fullWarmed).
         */
        const prev = lastWarmFrom.current;
        if (prev !== null && prev !== warmFrom) clearUrgentPrefetches();
        lastWarmFrom.current = warmFrom;
        // الموضع الذي ينتظره المستخدم — يقود وضع تركيز الطابور (يُضيَّق حتى يصل، ثم يُرخى).
        // المحيط يشارك في تركيز الطابور كبقيّة طبقات النسيج (بخلاف القمر الذي صوره
        // جاهزة لا رواستر تُنزَّل)، فالإطار الذي ينتظره المستخدم يتصدّر الصفّ.
        setReadinessFocus(satelliteAxis ? null : warmFrom);

        /*
         * **الإطار المطلوب نفسه أوّلاً، لا جيرانه.**
         *
         * كان يُستبعد بحجّة أن الطبقة تُحمّله مباشرةً. هذا صحيح لنسيج الطبقة المعروضة
         * وحده؛ أمّا بقيّة مدخلات الإطار نفسه — الرياح (تغذّي الجسيمات فوق كل الطبقات)،
         * والحرارة وk-index حين تكون الأمطار فعّالة — فلا يجلبها أحدٌ مباشرةً. فكانت
         * تُطلَب بعد جيران لن يراهم المستخدم قبلها، أي أن الساعة المقصودة تكتمل **بعد**
         * ما حولها. إدراجها في رأس الترتيب يعكس ذلك بلا أي تنزيل إضافي.
         */
        order.push(warmFrom);

        for (let d = 1; d <= lookAhead; d++) {
            if (warmFrom + d < warmFrames) order.push(warmFrom + d);
            // للخلف أيضاً: أثناء التشغيل خطوة واحدة تكفي (الاتجاه معروف)، أمّا خارجه
            // فالسحب يذهب في الاتجاهين بلا تنبّؤ — فنُبقي النافذة متماثلة.
            if ((isPlaying ? d === 1 : true) && warmFrom - d >= 0) order.push(warmFrom - d);
        }
        const warm = () => {
            for (const idx of order) {
                for (const type of types) {
                    const src = layerFrameUrl(rasterDir, type, idx);
                    // الشرط على **الفكّ** لا على «سبق طلبه»: التعبئة الخلفية تجلب البايتات
                    // بلا فكّ، فلو اكتفينا بسجلّ الطلبات لظنّت النافذة القريبة أن الإطار
                    // جاهز بينما لا نسخة مفكوكة له — وهو بالضبط الاختفاء الذي نعالجه.
                    if (isRasterImageReady(src) || isRasterImagePending(src)) continue;
                    queuePrefetchImage(src);
                }
            }
        };
        // ابدأ الإحماء فوراً بعد الإطار الحالي؛ التأخير السابق 350ms كان يسمح
        // للجسيمات بالوصول إلى الإطار التالي قبل اكتمال ألوان الطبقة.
        const timer = window.setTimeout(warm, 40);

        /*
         * ── لا إحماء لنسيج تراكم الأمطار هنا. أُزيل بعد قياسه. ──────────────────────
         *
         * أُضيف سابقاً بمنطق «ابنِ الإطارات الثلاثة القادمة مقدّماً». الفكرة تبدو صحيحة
         * لكن القياس نقضها: في trace للخيط الرئيسي ظهر `getAccumulatedFrameUrl` بـ**2.5
         * ثانية زمن ذاتي** (‏5.7% من العيّنات) موزّعة على ثلاثة أشكال مُجمَّعة — وهي أعلى
         * دالّة من كود التطبيق في الملفّ كلّه.
         *
         * السبب بنيوي لا عابر: كل بناء تراكم يحتاج ثلاثة حقول ساعية (idx-2..idx)، وكاش
         * الحقول الخام سقفه MAX_FIELDS = 16 حقلاً (‏ImageData بالدقة الكاملة ~4 م.ب لكل
         * واحد). الطبقة نفسها تطلب إطارين (الحالي والتالي)، فإضافة ثلاثة إطارات أخرى ترفع
         * مجموعة العمل إلى ستّة حقول هطول، ومعها حقول الحرارة وk-index لأنيميشن المطر
         * والبرق — فتتجاوز السعة ويبدأ **الطرد المتبادل**: حقلٌ يُطرد، فيعود البناء بـnull،
         * فيُعاد تحميله وتُعاد الحلقة على مليون بكسل من جديد. أي أن الإحماء صار يُنتج
         * العمل الذي وُضع ليمنعه.
         *
         * الإصلاح الصحيح لبطء هذه الطبقة كان في مكان آخر وقد نُفِّذ: إلغاء ترميز PNG وفكّه
         * (createImageBitmap مباشرةً من ImageData). أمّا نقل حلقة الجمع نفسها إلى Worker أو
         * إلى الـGPU فهو التحسين التالي الحقيقي — لا مضاعفة عدد مرّات تشغيلها على الخيط
         * الرئيسي.
         */

        /**
         * تخزين مؤقّت لكامل الأيام الخمسة في الخلفية — لا فقط النافذة القريبة أعلاه.
         *
         * سبب الإضافة: النافذة القريبة (حتى 16 إطاراً) تكفي التشغيل المتواصل خلال الدقائق
         * الأولى، لكن بعد تجاوزها (مثلاً بعد اليوم الأول) كانت بقيّة الأيام تُطلب فقط لحظة
         * وصول الشريط إليها فعلاً — تحميل + تأخّر مرئي، كما يحدث في مشغّل فيديو بلا تخزين
         * مؤقّت مسبق. الحل هنا **نفس مبدأ Windy**: تعبئة الكاش لكامل السلسلة فور معرفة
         * عددها، عبر طابور محدود التزامن (queuePrefetchImage، 6 اتصالات كحدّ أقصى) — فلا
         * ينافس الإطار الحالي (يُحمَّل مباشرةً لا عبر الطابور) ولا يُبطئ أوّل عرض، لكنه
         * يواصل التنزيل في الخلفية إلى أن تكتمل كل الأيام الخمسة بلا أي نقص في الدقّة (نفس
         * ملفّات PNG كاملة الدقّة، لا نُسخ مصغّرة).
         *
         * يعمل مرّة واحدة فقط لكل (مجلّد النموذج + مجموعة الطبقات) — لا يتكرّر مع كل نبضة
         * تغيّر إطار، وإلا أعاد فحص مئات الروابط في كل تغيّر.
         */
        const fullKey = `${warmDir}|${types.join(',')}`;
        if (fullWarmed.current !== fullKey) {
            /*
             * التفريغ هنا **لا في رأس المؤثّر**.
             *
             * كان `clearQueuedPrefetches()` يُستدعى في أوّل سطر من هذا المؤثّر، والمؤثّر
             * يُعاد تشغيله عند كل تغيّر ساعة (windHour.index ضمن اعتمادياته). فالنتيجة:
             * التعبئة الخلفية لكامل الأيام الخمسة تُدرَج مرّة واحدة عند الإقلاع (حارسها
             * fullWarmed يمنع إعادة الإدراج)، ثم تُمسح من الطابور عند أوّل خطوة ساعة —
             * ولا تعود أبداً. أي أن ميزة «كل الأيام مخزّنة مسبقاً» كانت معطّلة عملياً بعد
             * الدقيقة الأولى، وهو ما يُرى كتعثّر في الأيام البعيدة تحديداً.
             *
             * الغرض الأصلي من التفريغ صحيح — ألّا تحتلّ تنزيلاتُ النموذج السابق الشبكةَ
             * بعد التبديل — وهذا بالضبط ما يعنيه تغيّر fullKey. فنقلُه إلى هنا يحفظ الغرض
             * ويُلغي الأثر الجانبي.
             */
            clearQueuedPrefetches();
            fullWarmed.current = fullKey;
            /*
             * **يبدأ بعد اكتمال العرض الأوّل لا بعد نصف ثانية.** قياس الموقع الحيّ
             * (2026-09-26): التعبئة الكاملة تنزّل ~32 م.ب في الدقيقة الأولى (pressure16
             * ‏1.47 م.ب للإطار، wind ‏420 ك.ب)، وكانت تبدأ والإطار الأوّل وخطوط الضغط وبلاطات
             * القاعدة ما زالت تتنزّل — فتقاسمها عرض النطاق. الصفّ الخلفي يؤخّر **الترتيب**
             * داخل طابورنا فقط، لا ما يأخذه كل تنزيل جارٍ من الخطّ.
             *
             * المؤقّت لا يُلغى في تنظيف المؤثّر: المؤثّر يُعاد عند كل تغيّر ساعة، وحارس
             * fullWarmed قد ضُبط أعلاه، فإلغاؤه كان يعني ألّا تُدرَج التعبئة أبداً. بدلاً من
             * ذلك يتحقّق المؤقّت عند إطلاقه أن النموذج/الطبقات لم تتبدّل (fullKey).
             */
            window.setTimeout(() => {
                if (fullWarmed.current !== fullKey) return;
                for (let idx = 0; idx < warmFrames; idx++) {
                    for (const type of types) {
                        // نفس البانى الذي تستعمله النافذة القريبة والطبقة نفسها. تركُه على
                        // `rasterDir` كان يطلب `rasters/sst-anomaly_NNN.png` — مسارٌ لا وجود
                        // له، يردّ عليه خادم التطوير بصفحة SPA بحالة 200 فيبدو ناجحاً في
                        // شبكة المتصفّح بينما هو HTML يُغذّى إلى فاكّ صور.
                        const src = layerFrameUrl(rasterDir, type, idx);
                        if (warmedRasters.has(src)) continue;
                        warmedRasters.add(src);
                        if (warmedRasters.size > 2000) warmedRasters.clear();
                        // **الصفّ الخلفي**: 121 إطاراً × الأنواع = مئتا مهمّة أو أكثر. لو
                        // دخلت الصفّ العاجل (كما كانت) لاحتلّت الطابور، فتقف خلفها نافذةُ
                        // التشغيل القريبة التي يحتاجها المستخدم بعد ثوانٍ — وهو بالضبط سبب
                        // كون أوّل ساعتين سلستين ثم تعثّر البقيّة. راجع prefetchQueue.
                        queuePrefetchImage(src, 'background');
                    }
                }
            }, heatmapReady ? 3000 : 8000);   // بعد العرض الأوّل (أو مهلة إن تعثّر)
            return () => { window.clearTimeout(timer); };
        }
        return () => { window.clearTimeout(timer);};
    }, [rasterDir, useRaster, activeHeatmapType, visibleLayers.wind, hasWindSpeed, particlesOn, windHour.index, frameHours, heatmapReady, isPlaying, satelliteAxis,
        isOceanLayer, oceanUrl, oceanFrameCount, oceanMeta?.ext, oceanMeta?.generated_epoch, currentTimeIndex, isobarsEnabled, pressureField,
        radarAxis, radarManifest, radarTimes, zoomLevel]);

    // قاعدة الخريطة تتبع الطبقة الفعّالة (Zoom Earth): الحرارة على قاعدة فاتحة فستقية،
    // الرياح/الأمطار على قاعدة داكنة — بغضّ النظر عن مفتاح الوضع الداكن العام للواجهة.
    const activeLayerKey = visibleLayers.wind ? 'wind' : activeHeatmapType;
    // خطوط الضغط: مفتاح عالمي واحد يبقى كما تركه المستخدم عبر كل الطبقات (isobarsEnabled).
    const targetBaseDark = layerBaseIsDark(activeLayerKey, darkMode);
    // طبقات القاعدة الفاتحة (حرارة/رطوبة/…): نُبقيها داكنة حتى جهوز أوّل راستر، فلا تومض
    // القاعدة الفستقية العارية. مشروط بـ useRaster (المسار الذي يُطلق onReady فعلاً) كي لا
    // تعلَق داكنةً في مسار البلاطات أو بلا طبقة. القاعدة الداكنة أصلاً لا تتأثّر.
    const mapBaseDark = (!targetBaseDark && !heatmapReady && useRaster) ? true : targetBaseDark;
    // أمان: إن تعذّر تحميل الراستر (خادم بطيء/معطّل) نكشف القاعدة الفاتحة بعد مهلة كي لا تبقى
    // داكنة إلى الأبد. يبدأ العدّ فور جهوز الشريط (حين يصبح تحميل الراستر ممكناً).
    useEffect(() => {
        if (heatmapReady || !timelineReady) return;
        const t = window.setTimeout(() => setHeatmapReady(true), 5000);
        return () => window.clearTimeout(t);
    }, [heatmapReady, timelineReady]);

    // قالب ألوان الأمطار الثاني (model_2) نشط؟ عندها خلفية الخريطة رمادية بدل الزيتوني/
    // الكحلي المعتاد — طلب صريح لتمييز القالب بصرياً. القالب الأول (model_1) لا يغيّر شيئاً.
    const precipGrayBase = activeHeatmapType === 'precipitation' && isLayerColorTemplateActive('precipitation', 'model_2');

    const { handleMapLoad, handleMoveEnd } = useMapStyling({
        mapRef, darkMode: mapBaseDark, activeLayerKey, precipGrayBase, setMapBounds, setZoomLevel,
        satelliteActive: visibleLayers.satellite,
        radarActive: visibleLayers.radar,
        aerialImagery: aerialImageryEnabled,
    });

    const handleMapClick = (e: MapLayerMouseEvent) => {
        // أثناء القياس تخصّ النقرةُ الشكلَ المرسوم وحده: MeasureTool يلتقطها بنفسه،
        // ولو فتحنا لوحة الطقس معها لغطّت نصف الخريطة مع كل نقطة يضيفها المستخدم.
        if (useWeatherStore.getState().measureMode !== 'none') return;
        const { lat, lng } = e.lngLat;
        setCurrentLocation(lat, lng);

        // على الموبايل: نُظهر فقاعة معاينة أولاً بدل فتح اللوحة الكاملة فوراً — يفتحها
        // المستخدم بنفسه من سهم الفقاعة إن أراد التفاصيل الكاملة.
        if (isCompactViewport()) {
            setTapLocation({ lat, lon: lng });
            return;
        }
        setInfoPanelOpen(true);
    };

    // التلميح عند المرور (hover): نتبع الطبقة الفعّالة فعلياً (بما فيها الرياح) — وإلا
    // نعود إلى درجة الحرارة فقط حين لا توجد طبقة مرئية أصلاً. سابقاً كان يتجاهل طبقة
    // الرياح فيعرض الحرارة كقيمة أساسية حتى وطبقة الرياح هي المفعّلة.
    // تُقرأ القيمة مباشرةً من نُسج GFS المحلّية بلا أي طلب API.
    const hoverType: ForecastGridType = activeLayerKey ?? 'temperature';

    // هيتماب الأمطار يُلوَّن من نسيج التراكم الثلاثي لا المعدّل اللحظي الخام (راجع
    // usePrecipAccumPair أعلاه) — يطابق لون الخريطة رقم التلميح تماماً.
    const isPrecipActive = activeHeatmapType === 'precipitation';
    const precipPair = usePrecipAccumPair(isPrecipActive, currentTimeIndex, frameHours - 1);

    // جسيمات الرياح تبقى عاملةً فوق **كل** الطبقات، طبقة الأمطار منها.
    //
    // كانت تُطفَأ تلقائياً عند فتح الأمطار (بحجّة أنها تغطّي أنيميشن المطر/الثلج) ثم
    // تُستعاد عند الخروج. النتيجة عملياً: المستخدم يشغّل الجسيمات، يبدّل إلى الأمطار،
    // فتنطفئ من تحت يده بلا سبب ظاهر — ثم قد يُغلق الصفحة والحالة المحفوظة مطفأة،
    // فيعود ويجدها كذلك. مفتاح يقلبه النظام خلف ظهر المستخدم أسوأ من تراكب مزدحم:
    // الظهور اختياره هو، وWindy تُبقي الجسيمات مع طبقة الأمطار كذلك.

    const overlayPanelOpen = infoPanelOpen || layerControlsOpen || sidebarOpen || settingsOpen || modalOpen || cycloneDockOpen;
    // مفتاح الألوان المركزي ومبدّل النموذج يبقيان ظاهرين مع لوحتَي الطبقات والطقس: كلتاهما
    // على الجوّال ورقة سفلية تترك أعلى الشاشة فارغاً، فلا تراكب — وقراءة مفتاح الألوان مع
    // أرقام اللوحة هي الفائدة الأساسية. يُخفيان فقط مع اللوحات التي تملأ الشاشة فعلاً
    // (القائمة الجانبية/الإعدادات).
    // نفس المنطق يحكم **اللوجو** أيضاً (راجع صنفه أدناه): كان يستعمل `panel-open` الأوسع
    // فيختفي مع كل ورقة سفلية — الطبقات وتوقّعات النقطة والأعاصير — رغم أن أيّاً منها لا
    // يبلغ أعلى الشاشة حيث يجلس. صار يتبع هذه الراية: يظهر مع الأوراق السفلية، ويختفي
    // وحده مع اللوحات التي تملأ الشاشة فعلاً.
    const legendHidingPanelOpen = sidebarOpen || settingsOpen;
    // ── رابطٌ عميق: ?lat=&lon=&z= ────────────────────────────────────────────
    // صفحات المدن الساكنة (‏/weather/<city>/) تفتح الخريطة على مدينتها بهذا الرابط،
    // وهو كذلك ما يُنسخ حين يريد المستخدم مشاركة موضعٍ بعينه. يُقرأ **مرّة** عند
    // التركيب: بعدها الكاميرا للمستخدم لا للرابط، فلا تقفز الخريطة تحت يده.
    // ويُقيَّد بالحدود المعقولة — رقمٌ فاسد في الرابط يجب أن يُتجاهَل لا أن يرمي
    // الكاميرا إلى لا مكان.
    const deepLink = useRef<{ lon: number; lat: number; z: number | null } | null>(null);
    if (deepLink.current === null) {
        const q = new URLSearchParams(window.location.search);
        const lat = Number(q.get('lat')), lon = Number(q.get('lon'));
        const z = Number(q.get('z'));
        deepLink.current = (Number.isFinite(lat) && Math.abs(lat) <= 85
            && Number.isFinite(lon) && Math.abs(lon) <= 180 && q.has('lat') && q.has('lon'))
            ? { lon, lat, z: Number.isFinite(z) && z > 0 ? z : null }
            : { lon: NaN, lat: NaN, z: null };
    }
    const deep = Number.isFinite(deepLink.current.lat) ? deepLink.current : null;
    const initialLng = deep?.lon ?? currentLocation?.lon ?? 46.7;
    const initialLat = deep?.lat ?? currentLocation?.lat ?? 24.7;

    // بلا WebGL لا خريطة أصلاً: نمنع تركيب <Map> بدل تركيبه ليفشل — وإلّا رمى MapLibre
    // webglcontextcreationerror في الـ console وبقيت طبقاته وحلقات الرسم قائمة بلا فائدة
    // تحت رسالة الخطأ. كل الـ hooks أعلاه نُفِّذت، فالخروج المبكر هنا آمن.
    if (!webglOk) {
        return (
            <MapContext.Provider value={{ mapRef }}>
                <div className={`weather-map-container ${darkMode ? 'dark' : 'light'}`}>
                    <SplashScreen ready />
                    <WebGLUnsupported />
                </div>
            </MapContext.Provider>
        );
    }

    return (
        <MapContext.Provider value={{ mapRef }}>
            <div
                className={`weather-map-container ${darkMode ? 'dark' : 'light'} ${overlayPanelOpen ? 'panel-overlay-open' : ''} ${legendHidingPanelOpen ? 'legend-hiding-panel-open' : ''} ${layerControlsOpen ? 'layer-controls-open' : ''} ${modalOpen ? 'modal-open' : ''}`}
            >
                <LayerSidebar />

                {/* مؤشّر تحميل علوي عند تبديل النموذج/الطبقة (أسلوب Zoom Earth) */}
                {/* شاشة الافتتاح: تُبقي كل شيء مغطّى حتى تجهز الخريطة وأوّل إطار ملوّن،
                    فتظهر التسميات والحدود والألوان **معاً** بدل أن تسبق التسميات الخريطة. */}
                <SplashScreen ready={heatmapReady || splashExpired} />

                <MapLoadingToast
                    rasterDir={rasterDir}
                    activeType={activeHeatmapType}
                    windVisible={visibleLayers.wind}
                    modelName={effectiveModel}
                />

                {/* شعار عائم فوق الخريطة — يُخفى عند فتح البحث كي لا يغطّيه.

                    الموضع على الجوّال يتبع المفتاح: الشريط يشغل أعلى 30 بكسل، فيقف
                    الشعار تحته عند 38 (هامش 8). وحين لا مفتاح للطبقة (قمر/رادار)
                    يبقى ذلك الشريط فارغاً — فيرتفع الشعار بمقدار ارتفاعه بالضبط
                    إلى 8، محتفظاً بنفس الهامش من حافّة الشاشة. على سطح المكتب لا
                    أثر لهذا: المفتاح هناك أسفل يمين، لا فوق الشعار. */}
                {!searchOpen && (
                <div
                    className={`modal-open:hidden legend-hidden:max-[768px]:hidden ${darkMode ? 'bg-[#14181fb8]' : 'bg-white' } fixed top-[14px] left-16 right-auto z-[950] py-2 px-3 flex items-center rounded-xl shadow-[0_2px_16px_rgba(0,0,0,0.25)] pointer-events-none select-none whitespace-nowrap ${legendPresent ? 'max-[768px]:top-[38px]' : 'max-[768px]:top-[8px]'} max-[768px]:!right-auto max-[768px]:left-[12px] max-[768px]:py-1 max-[768px]:px-1.5 max-[768px]:rounded-lg`}
                    aria-label="Sky Radar"
                >
                    <img
                        src={darkMode ? '/sky-radar-logo-dark.svg' : '/sky-radar-logo-light.svg'}
                        alt="Sky Radar"
                        className="h-[35px] w-auto shrink-0 block max-[768px]:h-[30px]" width="140" height="35"
                    />
                </div>
                )}

                {/* قواعد الـDOM الذي تولّده MapLibre تُعبَّر عنها كمتغيّرات نسل هنا،
                    فلا حاجة لملفّ CSS من أجل عناصر لا نرسمها بأنفسنا. */}
                <div className="fixed inset-0 w-screen h-[100dvh] overflow-hidden [contain:size_layout_paint] bg-[#0a0e14] [&_:is(.maplibregl-map,.maplibregl-canvas-container,.maplibregl-canvas)]:absolute [&_:is(.maplibregl-map,.maplibregl-canvas-container,.maplibregl-canvas)]:inset-0 [&_:is(.maplibregl-map,.maplibregl-canvas-container,.maplibregl-canvas)]:[width:100%!important] [&_:is(.maplibregl-map,.maplibregl-canvas-container,.maplibregl-canvas)]:[height:100%!important] dark-mode:[&_.maplibregl-map]:bg-[#0a0e14]">
                    <Map
                        ref={mapRef}
                        mapStyle={DARK_MAP_STYLE}
                        initialViewState={{
                            longitude: initialLng,
                            latitude:  initialLat,
                            // الزوم المبدئي = Mi.home في Zoom Earth، مقيّداً بالحدود
                            zoom:      Math.min(getMaxZoom(), Math.max(getMinZoom(),
                                deep?.z ?? zoomLevel ?? getHomeZoom())),
                        }}
                        minZoom={getMinZoom()}
                        maxZoom={getMaxZoom()}
                        style={{ height: '100%', width: '100%' }}
                        attributionControl={false}
                        renderWorldCopies={true}
                        onLoad={handleMapLoad}
                        onMoveEnd={handleMoveEnd}
                        onClick={handleMapClick}
                    >
                        {/* طبقات البيانات (Source/Layer) داخل الخريطة */}
                        <SatelliteTileLayer displayEpoch={displayedEpoch} meta={satelliteMeta} />
                        {/* البيان يأتي من الأعلى لا من داخل الطبقة: هو نفسه مصدر محور
                            الشريط (‏useRadarFrames)، وجلبُه مرّتين يعني محوراً وصورةً قد
                            يفترقان دورةً كاملة. والزمن المعروض هو ما يختار الإطار. */}
                        <RadarTileLayer manifest={radarManifest} mosaic={radarMosaic}
                            coverageTiles={radarCoverageTiles} flow={radarFlow}
                            displayEpoch={displayedEpoch} times={radarTimes} />

                        {/* خطوط تساوي الضغط — فوق **كل** الطبقات بخيار محفوظ لكل طبقة.
                            الخطّ يُرسم تحليلياً في طبقة WebGL مستقلّة تقرأ نسيج الضغط
                            بنفسها، فلا يهمّها أي حقل ملوَّن تحتها.

                            واللون أبيض **دائماً**، كما عند Zoom Earth. وليس ذلك سعياً
                            للتباين: الأبيض عندهم لونٌ **محجوز** — لا شيء في سلالم الحقول
                            يبلغ الأبيض الناصع — فيبقى دلالةً على «خطّ» وحده، وتُقرأ
                            الخطوط بلا لبس مع الحقل تحتها مهما كانت قيمته. ولونٌ يتبدّل
                            مع الطبقة يكسر ذلك الحجز: يصير الأبيض مرّةً خطّاً ومرّةً قيمةً
                            في السلّم، فتفقد العين المفتاح الذي تقرأ به. */}
                        {isobarsEnabled && versionKnown && timelineReady && (
                            <IsobarWebGLLayer
                                /* ساعة النموذج لا فهرس المحور — **ثالث موضعٍ تقع فيه
                                   نفس العلّة**: عولجت في الجسيمات (windHour) ثم في
                                   الألوان (heatHour) وفاتت هنا. على محور الرادار
                                   `currentTimeIndex` فهرسٌ في شبكة خمس دقائق، فكانت
                                   خطوط الضغط تمرّ عبر أربع وعشرين ساعة **مستقبلية**
                                   في ثوانٍ بينما الرادار يعرض الساعتين الماضيتين —
                                   حقلٌ لا علاقة له باللحظة المعروضة. وقياس
                                   2026-09-20 أظهر ثمنه أيضاً: 12.3 ثانية في عامل
                                   الضغط داخل تسجيلٍ طوله عشر، فكّاً لشبكاتٍ لا
                                   تخصّ ما يُعرض. */
                                url={rasterUrl(rasterDir, `${pressureField}_${String(windHour.index).padStart(3, '0')}.png`)}
                                urlNext={rasterUrl(rasterDir, `${pressureField}_${String(Math.min(windHour.index + 1, frameHours - 1)).padStart(3, '0')}.png`)}
                                frameIndex={windHour.index}
                                p16={hasPressure16}
                                color={[1, 1, 1]}
                            />
                        )}
                        {/* نصوص الخطوط: H/L وقيمها، وأرقام القيم على الخطوط. تُشتقّ من
                            نفس الشبكة ونفس المستويات، فيستحيل بناءً أن يظهر حرف بلا حلقة. */}
                        <PressureAnnotationLayer
                            // ساعة النموذج نفسها التي تُرسم بها الخطوط — الأرقام تُشتقّ
                            // من نفس الشبكة، فاختلاف الفهرسين كان يعني رقماً من ساعة
                            // وخطّاً من أخرى.
                            timeIndex={windHour.index}
                            dir={rasterDir}
                            enabled={isobarsEnabled}
                            p16={hasPressure16}
                            // النصوص تتبع الخطّ لا حقلَ الألوان تحته: ربطُها بجهوز الهيتماب
                            // (heatmapReady) كان يؤخّر الأرقام ثوانيَ بعد ظهور الخطوط، حتى
                            // بدا أنها لا تظهر إلا بإطفاء الطبقة وإعادتها (عندها تكون الشبكة
                            // في الذاكرة فتحسب فوراً). الشرط الصحيح هو شرط الخطّ نفسه.
                            ready={versionKnown && timelineReady}
                            frameCount={frameHours}
                        />

                        {/* طبقة هيتماب الرياح — من راستر wind-speed الرمادي كامل الدقّة (مرمَّز
                            0..229.5 مطابقاً VALUE_RANGES['wind'])، مع سقوط على قناة R في نسيج RGB
                            المصغَّر للدورات القديمة التي لم تولّده بعد (ألوانها القديمة المنحرفة
                            تبقى مؤقتاً حتى أول دورة جديدة). مزج زمني ناعم لكل إطار (frameFraction)؛
                            الشبكة المفكوكة (windGrid) تبقى للجسيمات فقط (تحتاج قناتي U/V). */}
                        {visibleLayers.wind && versionKnown && timelineReady && (
                            <RasterHeatmapWebGLLayer
                                id="weather-heatmap-wind"
                                url={rasterUrl(rasterDir, `${hasWindSpeed ? 'wind-speed' : 'wind'}_${String(currentTimeIndex).padStart(3, '0')}.png`)}
                                urlNext={rasterUrl(rasterDir, `${hasWindSpeed ? 'wind-speed' : 'wind'}_${String(Math.min(currentTimeIndex + 1, frameHours - 1)).padStart(3, '0')}.png`)}
                                blend={windBlendAmount}
                                frameIndex={currentTimeIndex}
                                liveBlend
                                type="wind"
                                opacity={1}
                                valueRange={hasWindSpeed ? undefined : WIND_RGB_RANGE}
                                onReady={() => setHeatmapReady(true)}
                            />
                        )}
                        {/* الطبقات السلَّمية من نسيج عالمي واحد لكل إطار. هذا هو المسار السابق:
                            طلبات أقل، تبديل زمني أنعم، ولا يظهر التحميل مربّعاً مربّعاً عند التحريك.
                            حارس metaSettled ضروري (كطبقة الرياح تماماً): قبل وصول meta.json يكون
                            الربط بين موضع الشريط والإطار افتراضياً خاطئاً (runEpoch = الساعة الحالية،
                            nowFrameIndex = 0)، فتعرض الطبقة الإطار 0 كأنه "الآن" ثم تقفز فجأةً إلى
                            الإطار الصحيح حين يصل meta ويعيد ضبط الشريط — وهو ما كان يظهر كتغيّر ألوان
                            كامل مفاجئ واختلاف ألوان عند العودة لنفس التوقيت. */}
                        {/* بوّابة المحيط مانيفستُه هو، لا `versionKnown`/`timelineReady`:
                            هاتان تنتظران meta.json لدورة النموذج، وهي لا تحكم طبقة رصد
                            مستقلّة — انتظارها كان يؤخّر ظهورها بلا سبب (أو يمنعه تماماً
                            لو تعثّر توليد النموذج المختار). */}
                        {activeHeatmapType && useRaster && (isOceanLayer ? oceanFrameCount > 0 : (versionKnown && timelineReady)) && (
                            <RasterHeatmapWebGLLayer
                                id="weather-heatmap-scalar"
                                /* الأمطار تُلوَّن من نسيج **التراكم الثلاثي** حصراً.
                                 *
                                 * كان هناك سقوط إلى المعدّل اللحظي الخام ريثما يُبنى
                                 * التراكم — «بدل شاشة فارغة». وهو أسوأ من الفراغ: المعدّل
                                 * اللحظي كمّية مختلفة تماماً (مم/ساعة لحظية مقابل مجموع
                                 * ثلاث ساعات)، فيُلوَّن بشريط ألوان التراكم فتظهر ألوان لا
                                 * تطابق أي توقيت، ثم تُستبدل فجأة حين يجهز التراكم. هذا
                                 * حرفياً ما وُصف بـ«الألوان تعود لتوقيت خاطئ ثم تعدّل نفسها».
                                 *
                                 * لم يعد للسقوط مبرّر أصلاً: الطبقة صارت تُبقي آخر إطار
                                 * جاهز معروضاً أثناء الانتظار بدل أن تختفي (راجع
                                 * displayedUrl في RasterHeatmapGLLayer)، فالبديل الآن هو
                                 * حقلٌ أقدم بساعة — صحيحُ الوحدة والمعنى — لا حقلٌ خاطئ. */
                                url={isPrecipActive
                                    ? (precipPair?.url ?? '')
                                    : layerFrameUrl(rasterDir, activeHeatmapType === 'pressure' ? pressureField : activeHeatmapType,
                                        heatHour.index)}
                                urlNext={isPrecipActive
                                    ? (precipPair?.urlNext ?? '')
                                    : layerFrameUrl(rasterDir, activeHeatmapType === 'pressure' ? pressureField : activeHeatmapType,
                                        Math.min(heatHour.index + 1, (isOceanLayer ? oceanFrameCount : frameHours) - 1))}
                                blend={isPrecipActive ? colorBlendAmount : heatHour.fraction}
                                /* المزج المستمرّ: الكسر يصل من ساعة التشغيل بدقّة الشاشة
                                 * (‏60 قيمة/ثانية) بدل ستّ درجات عبر React — وهو مصدر
                                 * «التقطيع» الأكبر الذي كان يُرى عند تشغيل الشريط.
                                 *
                                 * **الأساس هو فهرس الزوج المحمَّل فعلاً، لا موضع الشريط.**
                                 * نسيج تراكم الأمطار يتأخّر عن الشريط ريثما يُبنى؛ فتمرير
                                 * موضع الشريط كان يحسب كسر ساعة N على زوج يمثّل N-1 وN —
                                 * إزاحة ساعة كاملة تنتهي بقفزة حين يلحق البناء. أمّا بقيّة
                                 * الطبقات فروابطها تتبع الشريط مباشرة، فالفهرسان واحد. */
                                frameIndex={isPrecipActive ? precipPair?.frameIndex : heatHour.index}
                                liveBlend
                                type={activeHeatmapType}
                                /* الترميز نفسه في الحالتين (‏955..1050)؛ الفرق بايت واحد
                                   أو اثنان — فشريط الألوان لا يتغيّر، والفكّ وحده يتغيّر. */
                                p16={activeHeatmapType === 'pressure' && hasPressure16}
                                opacity={activeHeatmapType === 'clouds' ? 0.6 : activeHeatmapType === 'precipitation' ? PRECIP_OPACITY : 1}
                                onReady={() => setHeatmapReady(true)}
                                // طبقة لا ملفّات لها على الخادم (حُذفت/لم تُولَّد) ⇒ الافتراضية
                                // (الرياح) بدل خريطة فارغة — «أي خلل صغير يُظهر خريطة بديلة».
                                onUnavailable={() => {
                                    const s = useWeatherStore.getState();
                                    if (!s.visibleLayers.wind) s.setActiveLayer('wind');
                                }}
                            />
                        )}

                        {/* بقية الطبقات العددية — WebGL مُبلّطة، تحميل مربّع‑مربّع */}
                        {activeHeatmapType && !useRaster && (
                            <TiledHeatmapWebGLLayer
                                id="weather-heatmap-scalar"
                                tiles={scalarTiles}
                                type={activeHeatmapType}
                                opacity={activeHeatmapType === 'clouds' ? 0.6 : activeHeatmapType === 'precipitation' ? PRECIP_OPACITY : 1}
                            />
                        )}

                        {/* جسيمات الرياح — WebGL (GPU) فوق أي طبقة فعّالة. مفتاح عالمي واحد
                            (particlesEnabled) يبقى عبر كل الطبقات؛ الإعدادات البصرية من طبقة الرياح. */}
                        {(() => {
                            const s = layerAnimationSettings.wind;
                            if (!windGridCur || !particlesOn || !s || s.reduceMotion) return null;
                            // حجم/سطوع الجسيمات يتبعان قاعدة خريطة الطبقة الفعّالة (فاتحة/داكنة)
                            // لا مفتاح الثيم العام — على الحرارة (قاعدة فاتحة) تكبر وتسطع قليلاً.
                            // reseedKey يتغيّر عند تغيير التوقيت **يدوياً** فقط (لا أثناء التشغيل
                            // السلس) فتُبنى الجسيمات من جديد عند السحب بدل انسيابها متّصلةً بالسابق.
                            // لا إخفاء ولا إعادة بذر عند سحب الشريط الزمني: الطبقة تمزج
                            // بين ساعتَي الرياح على الـ GPU وتُنعّم خطوات الشريط المتقطّعة،
                            // فتنساب الجسيمات في الحقل المتغيّر بلا انكسار — سلوك Zoom Earth.
                            // timeIndex/frameFraction من windHour لا من المحور: على محور القمر
                            // الصناعي يقيس المحور خطوات 30 دقيقة بينما نُسج الرياح ساعات نموذج،
                            // فتمرير كسر المحور هنا كان يمزج بين ساعتين بنسبة لا تخصّهما.
                            return <ParticleWebGLLayer
                                id="weather-particles"
                                windGridPrev={windGridPrev}
                                windGrid={windGridCur}
                                windGridNext={windGridNext}
                                timeIndex={windHour.index}
                                frameFraction={windHour.fraction}
                                // على محور التوقّعات فهرس ساعة الرياح = فهرس ساعة التشغيل،
                                // فتقرأ الجسيمات الزمن المستمرّ من نفس قناة اللون فيتزامنان
                                // تماماً. على محور القمر يُشتقّ الفهرس من زمن مطلق آخر
                                // (displayedEpoch) فيبقى المسار القديم عبر React هو الصحيح.
                                liveTime={!satelliteAxis}
                                settings={s}
                                darkMode={mapBaseDark}
                            />;
                        })()}

                        {/* مضلّعات تحذيرات NWS — تراكب مستقلّ لا يتبع الطبقة الفعّالة ولا
                            الشريط الزمني: التحذير قائمٌ الآن أو منتهٍ، وليس له إطارات. */}
                        <AlertPolygonLayer enabled={alertsEnabled} />

                        {/* البرق **جزءٌ من الطبقة لا مفتاحٌ مستقلّ** (كما في Windy): لا زرّ
                            لإخفائه. التوقّعيّ يلازم الأمطار دائماً (يميّز المطر الرعدي)، والمرصود
                            يلازم الرادار والقمر الصناعي — وكلاهما رصد، فلا يُسمّى «توقّعاً». */}
                        {/* سِرب صواعق من توقّع o-index (لا بيانات حيّة) — تظهر فقط مع طبقة هطول الأمطار */}
                        {/* مُطفأ بطلب العميل (2026-09-14): صواعق التوقّع فوق الأمطار وُصفت بأنها
                            «خطأ كبير جداً» وتضرّ بثقة التوقّع. المطر نفسه صحيح فيبقى. الكود
                            محفوظ لإعادته حين يتحسّن المعيار — غيّر LIGHTNING_ON_PRECIP. */}
                        <LightningLayer
                            enabled={LIGHTNING_ON_PRECIP && activeHeatmapType === 'precipitation'}
                            timeIndex={currentTimeIndex}
                            // نفس النسبة التي تُمرَّر لطبقة الألوان كـ blend أعلاه: بدونها
                            // كان السرب يقفز بالساعة بينما اللون ينساب، فتظهر صواعق فوق
                            // بقعة أزاح اللون المطر عنها حتى يلحقها الفهرس التالي.
                            frameFraction={colorBlendAmount}
                            modelName={effectiveModel}
                        />

                        {/* البرق **المرصود** — GOES GLM (الأمريكتان) + MTG-LI (أوروبا وأفريقيا
                            والشرق الأوسط) — يتعايش مع الطبقة التوقّعية أعلاه لا يحلّ محلّها:
                            تلك تمتدّ خمسة أيام وتغطّي العالم، وهذا رصدٌ حقيقي لكنه على «الآن»
                            وخارج آسيا وأستراليا (لا قمر برقٍ ثابتاً مفتوحاً فوقهما).
                            وهو يفرض شرطه بنفسه (currentTimeIndex === 0) فلا يُخلط رصدٌ
                            بتوقّع في صورة واحدة. */}
                        {/* على محورَي الرصد (الرادار/القمر) يتبع البرق **زمن الشريط**: كل ومضة
                            تظهر مع الصورة التي تقع في وقتها. وعلى «الآن» الساكن يُعاد تشغيله
                            حيّاً (epoch = null). ومحور التوقّع يُبقي الشرط القديم: رصدٌ على
                            «الآن» فقط، لا ومضاتٌ مرصودة فوق ساعة قادمة. */}
                        <GlmLightningLayer
                            enabled={(visibleLayers.radar || visibleLayers.satellite)
                                && (radarAxis || satelliteAxis || currentTimeIndex === nowFrameIndex)
                                /* لا برق في تنبّؤ الرادار: البرق رصدٌ فقط، وومضاتٌ فوق
                                   مطرٍ متوقَّع توحي بأنها ستقع هناك. */
                                && !(radarAxis && currentTimeIndex >= radarObserved)}
                            epoch={(radarAxis || satelliteAxis) && (isPlaying || currentTimeIndex !== nowFrameIndex)
                                ? displayedEpoch : null}
                            /* الطبقتان لا تجتمعان (المتجر يُطفئ إحداهما عند تشغيل الأخرى)،
                               فالرادار أوّلاً: أبيض صغير ومحصور في نطاق التغطية، وإلّا
                               أصفر أكبر وبلا حصر فوق صور الأقمار. */
                            mode={visibleLayers.radar ? 'radar' : 'satellite'}
                        />

                        {/* أنيميشن هطول متحرّك (مطر ينسكب/ثلج يتطاير) — شيدر منقول عن Zoom
                            Earth نفسه، فقط مع طبقة الأمطار. يقرأ نسيج التراكم نفسه الذي
                            يُلوَّن به الحقل، فيستحيل أن يظهر قطرة خارج منطقة الهطول.

                            نسيج o-index (stormUrl) يُمرَّر **فقط مع تشغيل البرق**: غرض
                            تخفيف بياض المطر فوق الخلايا الرعدية هو إبقاء الصواعق مقروءة
                            فوقه لا شيء آخر. وكان يُطبَّق دائماً، فتُعرض أشدّ مناطق الهطول —
                            قلب العاصفة نفسه — أبهتَ من محيطها الأضعف بلا سبب مرئي للمستخدم،
                            وهو عكس ما تصفه الطبقة. بلا الرابط يوقف الشيدر التخفيف كلياً
                            (u_hasStorm = 0). */}
                        <PrecipitationAnimationWebGLLayer
                            id={PRECIP_ANIM_LAYER_ID}
                            enabled={isPrecipActive}
                            precipUrl={precipPair?.url ?? null}
                            tempUrl={rasterUrl(rasterDir, `temperature_${String(currentTimeIndex).padStart(3, '0')}.png`)}
                            stormUrl={LIGHTNING_ON_PRECIP && activeHeatmapType === 'precipitation'
                                ? rasterUrl(rasterDir, `k-index_${String(currentTimeIndex).padStart(3, '0')}.png`)
                                : ''}
                        />

                        {/* تسميات المدن والدول — شارة كل مدينة تعرض قيمة الطبقة الفعّالة
                            (نفس طبقة التلميح: رياح، رطوبة، ضغط…) لا الحرارة دائماً. */}
                        {/* شارات المدن تُخفى على طبقة المحيط: كل مدينة على **يابسة**،
                            وشذوذ سطح البحر غير معرَّف هناك — فكانت الشارة تطبع قيمة
                            «لا بيانات» مفكوكةً (‏-5) تحت كل اسم مدينة، وهي قيمة خارج مدى
                            المفتاح المعروض (‎-4..+4) فلا يجد لها القارئ تفسيراً.
                            وهذا أيضاً ما تفعله كل الخرائط المرجعية: يابسة صامتة. */}
                        {!isOceanLayer && (
                            /* ساعة الحقل الملوَّن لا فهرس المحور — **رابع موضعٍ لنفس العلّة**
                               بعد الجسيمات والألوان والضغط. على محور الرادار كان الفهرس رقمَ
                               إطار رادار، فتقرأ شارات الحرارة ساعات تنبّؤ حتى 21 ساعة في
                               المستقبل فوق خريطةٍ تعرض الساعتين الماضيتين. ولأن timeIndex في
                               تبعيّات المؤثّر، كانت كل خطوة رادار تفكّ حقلاً جديداً وتهدم كل
                               الأسماء وتبنيها: 3439 مللي getImageData و3456 مللي innerHTML في
                               تتبّع 2026-09-21 — عطبٌ واحد ظهر بندين. heatHour لا windHour:
                               على المحيطات الفهرس يومٌ وملفّاتها مفهرسة به. */
                            <ArabicCityLabels timeIndex={heatHour.index} type={hoverType} darkBase={mapBaseDark} />
                        )}

                        {/* متتبع الأعاصير والحرائق — نمرّر الزمن الصالح للإطار المعروض كي تلاحق
                            أيقونة الإعصار مركزه على مسار التوقّع بدل بقائها مثبّتة عند "الآن". */}
                        {/* استخدم زمن إطار الـ timeline نفسه، لا افتراض +1 ساعة لكل فهرس؛
                            بعض دورات الراستر تبدأ بإزاحة/فاصل مختلف، وهذا كان يحرّك
                            علامة الإعصار إلى نقطة خاطئة على المسار. */}
                        {/* displayedEpoch لا forecastTimes[currentTimeIndex]: الأخير يفترض أن
                            الفهرس ساعةُ توقّع، وهو صحيح على محور النماذج فقط. على محور القمر
                            الصناعي يعني الفهرس خطوة 30 دقيقة في سلسلة صور، فكانت علامة الإعصار
                            تقفز إلى زمن لا علاقة له بالمعروض. displayedEpoch هو الزمن المطلق
                            المعروض فعلاً على أي محور — وهو ما تريده العلامة بالضبط. */}
                        <CycloneTracker frameValidEpochMs={displayedEpoch * 1000} />
                        {WildfireLayer && <WildfireLayer />}

                        {/* شريط الأدوات الأيمن */}
                        <RightToolbar />

                        {/* علامة الموقع المحدد */}
                        {currentLocation && (
                            <Marker
                                longitude={currentLocation.lon}
                                latitude={currentLocation.lat}
                                anchor="center"
                            >
                                <div className="w-5 h-5 bg-[#ff4757] border-[3px] border-white rounded-full shadow-[0_0_0_4px_rgba(255,71,87,0.3)] animate-marker-pulse" />
                            </Marker>
                        )}
                    </Map>

                    {/* أدوات القياس (مسافة/مساحة) — تُرسم فوق الخريطة لا داخل مكدّس طبقاتها */}
                    {MeasureTool && <MeasureTool />}

                    {/* تلميح القيمة عند مرور المؤشّر (أسلوب Zoom Earth) */}
                    {/* الرادار يسبق الطبقة السلَّمية في التلميح: هو ما يراه المستخدم
                        تحت المؤشّر فعلاً. و`windTimeIndex` يُمرَّر لأن `currentTimeIndex`
                        على محور الرادار فهرسُ إطارٍ بدقيقتين لا ساعةُ نموذج. */}
                    <HoverValueTooltip
                        type={hoverType}
                        // ساعة الحقل لا فهرس المحور: على الرادار والقمر يعود التلميح إلى
                        // الحرارة، وكان يقرؤها من ساعة تنبّؤٍ بعيدة (راجع شارات المدن).
                        timeIndex={heatHour.index}
                        radar={visibleLayers.radar && radarManifest
                            ? { domains: radarManifest.domains, epoch: Math.floor(displayedEpoch) }
                            : null}
                        windTimeIndex={windHour.index}
                    />

                    {/* فقاعة معاينة الموبايل بعد النقر — تختفي بمجرد فتح اللوحة الكاملة أو أي لوحة أخرى */}
                    {tapLocation && !overlayPanelOpen && (
                        <MapTapCallout
                            location={tapLocation}
                            type={hoverType}
                            // نفس التلميح: ساعة الحقل، لا فهرس محور الرادار أو القمر.
                            timeIndex={heatHour.index}
                            onExpand={() => { setInfoPanelOpen(true); setTapLocation(null); }}
                        />
                    )}

                    {/* لوحة التحكم بالطبقات */}
                    <LayerControls />
                    <OverlayQuickControls />

                    {/* مفتاح الألوان المركزي */}
                    <CentralLegend />

                    {/* ختم الوقت — يظهر فقط حين يُشغّله المستخدم، ويسافر مع صورة الشاشة
                        إلى مواقع التواصل حيث لا شريط زمني يشرح اللحظة المعروضة. */}
                    <TimestampBadge
                        displayEpoch={displayedEpoch}
                        /* على محاور الرصد آخر علامة **هي** أحدث رصد؛ وعلى محور التوقّع
                           آخر علامة بعد خمسة أيام، فالتحديث هو زمن دورة النموذج.

                           ومحور الرادار استثناءٌ صار لازماً: آخر علامة فيه لم تعد رصداً
                           بل نهاية امتداد التوقّع (ستّ ساعات قُدُماً). فكان الختم يُعلن
                           «آخر تحديث 5:38 م» ووقتها 11:38 ص — أي يَعِد بحداثةٍ لا وجود
                           لها، وهو أسوأ ما يفعله ختمٌ وُضع ليُصدِّق الصورة المنشورة.
                           فنأخذ آخر رصدٍ حقيقي من `radarTimes` لا من المحور الممتدّ. */
                        updatedEpoch={radarAxis ? (radarTimes[radarObserved - 1] ?? null)
                            : (satelliteAxis || oceanAxis) ? (timelineTimes.at(-1) ?? null)
                                : (runEpoch || null)}
                        legendPresent={legendPresent}
                    />

                    {/* خلفية معتمة قابلة للنقر لإغلاق لوحة الطقس على الموبايل (نافذة منبثقة هناك) —
                        على الديسكتوب تبقى مخفية عبر CSS فلا تتعارض مع اللوحة الجانبية الثابتة. */}
                    {/* تتوقّف فوق شريط الوقت مباشرةً كي يبقى السحب عليه ممكناً واللوحة مفتوحة. */}
                    {infoPanelOpen && (
                        <div className="hidden max-[768px]:block max-[768px]:fixed max-[768px]:inset-0 max-[768px]:bottom-[calc(74px+env(safe-area-inset-bottom))] max-[768px]:z-[2040] max-[768px]:bg-transparent" onClick={() => setInfoPanelOpen(false)} />
                    )}

                    {/* لوحة معلومات الطقس — تُفتح بمجرد الضغط على إحداثية، وتعرض حالة تحميل/خطأ
                        حتى لو لم تصل البيانات بعد (بدل ألا تظهر إطلاقاً عند فشل/تأخّر الـ API). */}
                    {infoPanelOpen && (
                        <WeatherInfoPanel
                            weatherData={weatherData}
                            // ساعة **النموذج** لا موضع الشريط: على محورَي الرادار والقمر
                            // يقيس الشريط دقائق لا ساعات، فتمريره كفهرس ساعة كان يجعل
                            // لوحة «كل ساعة» تقفز ساعةً عند كل إطار رادار (دقيقتان).
                            currentTimeIndex={windHour.index}
                            targetEpoch={forecastTimes[windHour.index] ?? forecastTimes[0]}
                            frameCount={forecastTimes.length}
                            location={currentLocation}
                            onRetry={() => currentLocation && setCurrentLocation(currentLocation.lat, currentLocation.lon)}
                        />
                    )}

                    {/* مؤشر الموقع — يمين **فيزيائي** فوق زرّ الطبقات (عمود الخرائط يميناً في
                        اللغتين). كان end-[18px] فينقلب في العربية يساراً تحت الشعار. */}
                    <div className="absolute top-[15px] right-[18px] z-[700] flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-[var(--panel-bg)] backdrop-blur-[16px] border border-[var(--panel-border)] text-inherit light:text-[var(--text-primary)] shadow-[0_2px_12px_rgba(0,0,0,0.3)] light:shadow-[0_2px_12px_rgba(0,0,0,0.08)] max-[768px]:hidden">
                        <FiMapPin />
                        <span className="[direction:ltr] [unicode-bidi:isolate]">
                            {currentLocation
                                ? `${currentLocation.lat.toFixed(2)}°, ${currentLocation.lon.toFixed(2)}°`
                                : t('location.none')}
                        </span>
                    </div>

                    {/* عمود الأزرار العائمة (جسيمات/إيزوبار/حرائق/أعاصير/بروق) أُزيل:
                        خمسة أزرار كانت تحتلّ حافّة الخريطة دائماً، وانتقلت جميعها إلى
                        قسم "تراكبات" داخل اللوحة الجانبية — فتبقى الخريطة خالية ما لم
                        يفتحها المستخدم. المكوّنات نفسها (ParticleToggle وأخواتها) لم
                        تعد تُركَّب هنا؛ منطقها السياقي انتقل إلى LayerControls. */}

                    {/* مبدّل النموذج (GFS/ECMWF) ظاهر دائماً على الصفحة الرئيسية — أسلوب Zoom Earth */}
                    {/* لا نماذج للقمر الصناعي — صور رصد مباشر لا مُخرَج GFS/ECMWF/ICON. */}
                    {!visibleLayers.satellite && <ModelSwitch />}

                    {/* شريط الوقت — مستقلّ عن Open-Meteo: محوره ساعات توقّع GFS (نُسج النسيج) */}
                    <TimeSlider
                        times={timelineTimes}
                        currentTimeIndex={currentTimeIndex}
                        isPlaying={isPlaying}
                        // بيانات الرياح ساعية المصدر فعلياً (لا مزج بينها إلّا حين GFS ولحدّ
                        // hourly_through — راجع canBlendWind أعلاه)، فالسحب بخطوة 10 دقائق
                        // كان يوهم بدقّة زمنية غير موجودة: يقف المؤشر على :10/:20... بينما لا
                        // تغيّر فعلياً في حقل الرياح إلا عند الساعة الكاملة. خطوة ساعية مطابقة
                        // للواقع، وتمييزها بصرياً (عبر hourlyMode) يوضّح للمستخدم سبب اختلاف
                        // سلوك السحب هنا عن الحرارة بدل أن يبدو خللاً.
                        hourlyMode={visibleLayers.wind}
                        // المحور اليومي لطبقة المحيط: مقطع = يوم، وتسمية = تاريخ بلا ساعة.
                        dailyMode={oceanAxis}
                        forecastFrom={radarAxis && radarObserved < radarTimes.length ? radarObserved : undefined}
                    />
                </div>

                {/* لوحة الإعدادات */}
                {settingsOpen && SettingsPanel && <SettingsPanel onClose={() => setSettingsOpen(false)} />}

                {/* بوابة نافذة المدينة */}
                <div id="city-modal-portal" />
            </div>
        </MapContext.Provider>
    );
}
